import en from './en';
import { pluralize, formatDate } from '../util/util';
import {
  ClearingCreatedActivity,
  EventSaleChangeActivity,
  InviteUserActivity,
  MembershipChangedEvent,
  UserAddedToCompanyActivity,
  UserRemovedFromCompanyActivity,
} from '../model/activity';
import { Membership } from '../model/membership';
import { userNameAndOrEmail } from '../model/user';
import { I18n } from './translate';

const de: typeof en = {
  actions: {
    add: (item: string) => `${item} hinzufügen`,
    assign: (item: string) => `${item} assoziieren`,
    book: (item: string) => `${item} buchen`,
    cancel: (item: string) => `${item} abbrechen`,
    close: (item: string) => `${item} schließen`,
    continue: 'weiter',
    create: (item: string) => `${item} erstellen`,
    debug: 'Debug',
    delete: (item: string) => `${item} löschen`,
    display: (item: string) => `${item} anzeigen`,
    displaySelectedOnly: 'nur Selektierte zeigen',
    download: 'Download',
    downloadAsCSV: 'Download als CSV',
    downloadAsJSON: 'Download als JSON',
    duplicate: (item: string) => `${item} kopieren`,
    edit: (item: string) => `${item} bearbeiten`,
    loading: (item: string) => `Lade ${item}`,
    place: (item: string) => `${item} platzieren`,
    reassign: 'Zuordnung ändern',
    placeAndBook: 'Platzieren & Buchen',
    placeAndSend: 'Platzieren & Senden',
    reload: 'Neu laden',
    export: (item: string) => `${item} exportieren`,
    remove: (item: string) => `${item} entfernen`,
    save: (item: string) => `${item} speichern`,
    scan: (item: string) => `${item} scannen`,
    search: (item: string) => `${item} suchen`,
    send: (item: string) => `${item} senden`,
    select: (item: string) => `${item} auswählen`,
    start: (item: string) => `${item} starten`,
    stopSale: 'Verkauf beenden',
    upload: (item: string) => `Upload ${item}`,
    view: (item: string) => `${item} anzeigen`,
    selected: (item: string) => `${item} ausgewählt`,
    loadMore: 'Mehr laden',
  },
  component: {
    picker: {
      startTime: 'Startzeit',
      endTime: 'Endzeit',
    },
    artistSelect: {
      loading: 'Künstler*innen werden geladen …',
    },
    avatarUpload: {
      dragAndDrop: 'Bild hier ablegen oder klicken',
    },
    intervalPicker: {
      datePresets: {
        today: 'Heute',
        yesterday: 'Gestern',
        weekToDate: 'Aktuelle Woche',
        lastWeek: 'Letzte Woche',
        monthToDate: 'Aktueller Monat',
        lastMonth: 'Letzter Monat',
        last30Days: 'Letzte 30 Tage',
        yearToDate: 'Seit Jahresbeginn',
        lastYear: 'Letztes Jahr',
        totalPeriod: 'Gesamter Zeitraum',
        custom: 'Benutzerdefiniert',
      },
      interval: {
        hour: 'Stündlich',
        day: 'Täglich',
        week: 'Wöchentlich',
        month: 'Monatlich',
      },
      selectPeriod: 'Zeitraum auswählen',
      intervalText: 'Intervall',
      apply: 'Anwenden',
    },
    salesOverTime: {
      title: 'Verkäufe im Laufe der Zeit',
      day: 'Tägliche Verkäufe',
      week: 'Wöchentliche Verkäufe',
      month: 'Monatliche Verkäufe',
      hour: 'Stündliche Verkäufe',
      totalSales: 'Gesamtverkäufe',
    },
    fileUpload: {
      selectFile: 'Datei auswählen',
    },
    pagination: {
      linesPerPage: 'Zeilen pro Seite',
      outOf: (current: string | number, total: string | number) => `${current} von ${total}`,
    },
    confirmation: {
      title: 'Warnung',
      text: 'Sind Sie sicher, dass Sie diese Aktion durchführen wollen?',
      confirm: 'Bestätigen',
      cancel: 'Abbrechen',
    },
    buildCheck: {
      outdatedVersionTitle: 'Veraltete Version',
      outdatedVersionDescription:
        'Du benutzt eine ältere Version dieser Web-Applikation. Lade die Seite neu, um auf die neuste Version zu wechseln',
      outdatedVersionButton: 'Seite Aktualisieren',
    },
    heatmap: {
      allSales: 'Alle Verkäufe',
    },
    turnover: {
      title: 'Umsätze',
      noData: 'Für den ausgewählten Zeitraum sind keine Daten verfügbar',
      totalSales: 'Gesamtumsatz',
      total: 'Gesamt',
      period: {
        day: 'Täglicher Umsatz',
        hour: 'Stündlicher Umsatz',
        month: 'Monatlicher Umsatz',
        week: 'Wöchentlicher Umsatz',
      },
    },
    ordersPlaced: {
      title: 'Aufgegebene Bestellungen',
      noData: 'Für den ausgewählten Zeitraum sind keine Daten verfügbar',
      total: 'Gesamt',
      tooltip: 'Alle aufgegebenen Bestellungen, ohne retournierte oder stornierte Bestellungen.',
      period: {
        day: 'Tägliche Bestellungen',
        hour: 'Stündliche Bestellungen',
        month: 'Monatliche Bestellungen',
        week: 'Wöchentliche Bestellungen',
      },
    },
    averageOrderValue: {
      title: 'Durchschnittlicher Bestellwert',
      noData: 'Für den ausgewählten Zeitraum sind keine Daten verfügbar',
      tooltip:
        'Die Berechnung erfolgt ausschließlich auf Basis von Merchandise-Produkten. Bestellungen können auch andere Produkte enthalten, beispielsweise wenn diese über krasserstoff.com oder in einem anderen Shop über den Warenkorb verkauft werden. Bestellungen können auch Tickets enthalten. Andere Waren, Tickets und Versandkosten werden bei dieser Berechnung nicht berücksichtigt. Die Berechnung basiert auf Nettopreisen.',
      period: {
        day: 'Täglicher durchschnittlicher Bestellwert',
        hour: 'Stündlicher durchschnittlicher Bestellwert',
        month: 'Monatlicher durchschnittlicher Bestellwert',
        week: 'Wöchentlicher durchschnittlicher Bestellwert',
      },
    },
    sessions: {
      title: 'Sitzungen',
      tooltip:
        'Eine Shopping-Sitzung ist eine Reihe von Aktionen, die von einem Benutzer in einem bestimmten Zeitraum ausgeführt werden.',
      total: 'Gesamt',
      period: {
        day: 'Tägliche Besucher',
        hour: 'Stündliche Besucher',
        month: 'Monatliche Besucher',
        week: 'Wöchentliche Besucher',
      },
      noData: 'Für den ausgewählten Zeitraum sind keine Daten verfügbar',
    },
    countries: {
      title: 'Länder',
      turnover: 'Umsatz',
      orderCount: 'Anzahl der Bestellungen',
      sessionCount: 'Anzahl der Sitzungen',
      noData: 'Für den ausgewählten Zeitraum sind keine Daten verfügbar',
      noCountry: 'n. v.',
    },
    pointsOfSale: {
      title: 'Verkaufsstelle',
      turnover: 'Umsatz',
      averageOrderValue: 'Durchschnittlicher Auftragswert',
      orderCount: 'Anzahl der Bestellungen',
      noData: 'Keine Daten für den ausgewählten Zeitraum verfügbar',
      otherShop: 'Divers (z.B. krasserstoff.com, Kundensupport)',
    },
    products: {
      title: 'Produkte',
      turnover: 'Umsatz',
      sales: 'Absatz',
      noData: 'Keine Daten für den ausgewählten Zeitraum verfügbar',
    },
    productImage: {
      downloadOriginal: 'Originaldatei herunterladen',
    },
    meta: {
      card: {
        title: 'Meta',
      },
      modal: {
        title: 'Meta bearbeiten',
        fieldName: 'Meta',
      },
    },
    userLoginBarcode: {
      title: 'Login-Barcode',
      barcodeExists: 'Der Login-Barcode ist vorhanden.',
      noBarcode: 'Kein Login-Barcode vorhanden.',
      noPermission: 'Du hast keine Berechtigung, den Login-Barcode zu sehen.',
      download: 'Login-Barcode herunterladen',
      generate: {
        title: 'Login-Barcode generieren',
        areYouSure: 'Möchtest du wirklich einen neuen Login-Barcode generieren? Der vorherige Barcode wird ungültig.',
      },
      remove: {
        title: 'Login-Barcode entfernen',
        areYouSure: 'Möchtest du wirklich den Login-Barcode entfernen? Der Code wird dadurch ungültig.',
      },
    },
  },
  errors: {
    errorLoadingPage:
      'Es ist ein Fehler aufgetreten. Bitte kontaktiere den Administrator oder versuch es später erneut.',
    errorLoadingItem: (item: string) => `Beim laden von ${item} ist ein Fehler aufgetreten.`,
    errorLoadingList: 'Die Liste konnte nicht geladen werden',
    errorPublishRange: 'Der Vorverkaufstart kann nicht vor dem Ankündigungszeitpunkt liegen.',
    errorStartEndRange: 'Das Enddatum kann nicht vor dem Anfangsdatum liegen.',
    generalError: 'Es ist ein Fehler aufgetreten. Bitte versuch es später noch einmal.',
    invalidLink: 'Ungültiger reset token, bitte kontaktiere einen Administrator.',
    invalidUserMsg: 'Passwort oder E-Mail sind nicht korrekt.',
    itemAlreadyScanned: 'Produkt wurde schon gescannt.',
    itemNotFound: (item: string) => `${item} konnte nicht gefunden werden`,
    itemNotInDelivery: 'Produkt nicht in dieser Bestellung.',
    itemNotInList: 'Element ist nicht in der Liste.',
    moreResults: (amount?: string | number) =>
      `${amount ? `${amount} weitere Ergebnisse` : 'Es gibt weitere Ergebnisse'}. Bitte grenze deine Suche weiter ein.`,
    noItemsforThisCollection: (collection: string) => `Keine Suchergebnisse in ${collection}`,
    noResults: 'Für diese Suche gibt es keine passenden Einträge',
    notFound: 'not found',
    notValidJSON: 'Please enter a valid JSON',
    offline: 'Bitte überprüfe deine Internetverbindung.',
    passwordMismatch: 'Die Passwörter sind nicht identisch.',
    renderError: 'Es ist ein Fehler aufgetreten.',
    required: 'Diese Feld darf nicht leer sein',
    submitError: 'Es gab einen Fehler bei der Übermittlung',
    uploadFile: 'Es gab einen Fehler beim Upload der Datei',
    uploadImage: 'Es gab einen Fehler beim Upload des Bildes',
    noPossibleActions: 'keine Aktionen möglich',
    notTrackingCode: (query: string) => `Die Eingabe ${query} scheint kein Tracking Code zu sein.`,
    'invalid-website-format': 'Muss eine gültige Url sein',
    'invalid-email-address': 'Muss eine gültige E-Mail Adresse sein',
    'required-field': 'Diese Feld darf nicht leer sein',
    'password-missmatch': 'Die Passwörter müssen gleich sein.',
    'invalid-json': 'Dieses Feld muss als JSON formatiert werden.',
    'regex-failed': 'Bitte geben Sie einen gültigen Wert ein.',
    noShopAssigned: 'kein Shop zugeordnet',
    requiredVariantsDescription: 'Alle Varianten sollten eine Beschreibung haben.',
    voucher_codes_min_1: 'Die Anzahl der Gutscheincodes darf nicht kleiner als 1 sein.',
    voucher_codes_max_1000: 'Die Anzahl der Gutscheincodes darf maximal 1000 sein.',
  },
  refund: {
    success: 'Rückerstattung erfolgreich',
    maxAmount: (amount: number) => `Der maximale Rückerstattungsbetrag sollte zwischen 0 und ${amount} liegen`,
    amount: 'Betrag',
    title: 'Zahlung zurückerstatten',
    attention: 'Achtung:',
    description:
      'Wenn Sie auf „erstatten“ klicken, wird der eingegebene Betrag direkt auf das Kundenkonto zurücküberwiesen.',
    refund: 'erstatten',
  },
  general: {
    link: 'Verknüpfen',
    unlink: 'Verknüpfung aufheben',
    and: 'und',
    absolute: 'Absolut',
    all: 'Alle',
    amount: 'Anzahl',
    archived: 'Archiviert',
    backHome: 'Zurück zu Home',
    cantBeEdited: `Dieses Feld kann nicht bearbeitet werden`,
    color: 'Farbe',
    collection: 'Veranstaltungen',
    confirmDelete: (item: string) => `Soll ${item} wirklich gelöscht werden?`,
    confirmDuplicate: (item: string) => `Möchtest du das ${item} wirklich duplizieren?`,
    confirmStopSale: 'Möchtest Du den Verkauf wirklich beenden?',
    content: 'Inhalt',
    count: 'Anzahl',
    day: 'Tag',
    description: 'Beschreibung',
    details: 'Details',
    english: 'Englisch',
    englishShort: 'EN',
    feature: 'Features',
    error: 'Fehler',
    example: 'z.B.',
    footer: 'Fußzeile',
    generalInformation: 'Allgemeine Information',
    german: 'Deutsch',
    germanShort: 'DE',
    headline: 'Kopfzeile',
    itemsForsCollection: (item: string, collection: string) => `${item} für ${collection}`,
    itemsInThisCollection: (item: string, collection: string) => `${item} in dieser ${collection}`,
    legacyId: 'ID (altes System)',
    logout: 'Logout',
    material: 'Material',
    more: (amount: string | number, collection: string) => `${amount} Ergebnisse in ${collection}`,
    motive: 'Motiv',
    new: 'Neu',
    next: 'weiter',
    no: 'Nein',
    notArchived: 'Nicht archiviert',
    now: 'Jetzt',
    open: 'Öffnen',
    optional: 'Optional',
    overview: 'Überblick',
    productSafetyDescription: 'Angaben zur Produktsicherheit',
    rate: 'Satz',
    row: 'Zeile',
    rows: 'Zeilen',
    saved: 'Gespeichert',
    refund: 'Stornieren',
    return: 'Einbuchung',
    selected: 'Ausgewählt',
    since: 'seit',
    slug: 'Slug',
    subtitle: 'Untertitel',
    text: 'Text',
    title: 'Titel',
    total: 'Gesamt',
    type: 'Typ',
    url: 'URL',
    actions: 'Aktionen',
    locale: 'Sprache',
    lifeTime: 'Gesamt',
    lastSixMonths: '6 Mon. Ø',
    lastThirtyDays: 'Letzte 30 Tage',
    today: 'Heute',
    lastHour: 'Letzte Stunde',
    start: 'Abrechnen',
    sendViaEmail: 'Per Email senden',
    name: 'Name',
    noData: 'Keine Daten',
    noDataToShow: 'Keine Daten anzuzeigen',
    percentage: 'Prozentual',
    percent: 'Prozent',
    reactivate: 'Reaktivieren',
    yes: 'Ja',
    withoutNotification: 'ohne Benachrichtigung',
    connectedToAPI: (url: string) => `Du bist gerade mit verbunden mit ${url}.`,
    showing: 'Zeige',
    seeMore: 'mehr anzeigen',
    apiToken: 'API Token',
    calendarFeedUrl: 'iCal Feed',
    success: 'Erfolg',
    notAvailable: 'N/A',
    genders: {
      soldToMUsers: 'Männlich',
      soldToFUsers: 'Weiblich',
      soldToXUsers: 'Unbekannt / Divers',
    },
    rowsPerPage: 'Ergebnisse pro Seite:',
    warning: 'Warnung',
  },
  imports: {
    success: 'Import erfolgreich',
    error: 'Import fehlgeschlagen',
  },
  model: {
    ticketEmailTemplate: {
      cardTitle: 'Email-Template anpassen',
      tags: {
        custom: 'Eigenes',
        default: 'Default',
      },
      emailLabel: 'Auf E-Mail antworten',
      noEmailSet: 'Keine Antwort-E-Mail eingerichtet',
      previewLabel: 'Vorschau',
      previewEmail: 'E-Mail-Vorschau',
      emailTemplate: 'Email-Template',
      contentAndStylePreview: 'Inhalts- und Stilvorschau',
      modal: {
        title: 'E-Mail-Vorlage anpassen',
        emailLabel: 'Antwort-E-Mail',
        fieldLabel: 'E-Mail-Adresse',
        switcher: {
          label: 'Benutzerdefinierte E-Mail-Vorlage verwenden?',
          yes: 'Ja',
          no: 'Nein',
        },
        templateVariables: 'Platzhalter',
        firstName: 'Vorname',
        lastName: 'Nachname',
        email: 'E-Mail',
        phone: 'Telefon',
        ticketCount: 'Anzahl Tickets',
        emailText: 'E-Mail Text',
      },
    },
    activity: {
      _plural: 'Aktivitäten',
      reply: 'antworten',
      writeComment: 'Schreibe einen Kommentar',
      writeReply: 'Schreibe eine Antwort',
      showDetails: 'Details anzeigen',
      type: {
        activateProductOnKulturpass: ({ modifications: { active } }: { modifications: { active: boolean } }) =>
          active ? 'Activated offer on Kulturpass' : 'Deactivated offer on Kulturpass',
        archived: 'Archiviert',
        venueLayoutArchived: 'Saalplan archiviert',
        venueLayoutUnarchived: 'Saalplan wiederhergestellt',
        vatOverrideAccepted: 'hat die Diskrepanz in der USt zwischen Vertrag und Produkt bestätigt.',
        stripeWebhookProcessed: 'Stripe Webhook erhalten',
        stripeChargePerformed: 'Stripe Zahlung durchgeführt',
        itemsAddedToCart: 'Produkte zum Warenkorb hinzugefügt',
        addItems: 'Produkte zum Warenkorb hinzugefügt',
        createStripePayment: 'Stripe-Zahlung angefordert',
        confirmOrderPayment: 'Bestätigungsseite angezeigt',
        paymentConfirmationSent: 'Zahlungsbestätigung gesendet',
        itemsRemovedFromCart: 'Produkte vom Warenkorb entfernt',
        placeOrder: 'Bestellung gesendet',
        cartCreated: ({ modifications }: { modifications: { ip?: string; userAgent?: string; source?: string } }) => {
          if (!modifications?.ip) return 'Warenkorb erstellt';

          const source = modifications.source ?? 'unverified';
          const device = modifications.userAgent ?? 'einem unbekannten Gerät';
          return `Warenkorb erstellt von ${modifications.ip} (${source}) mit ${device}`;
        },
        cartUpdated: 'Warenkorb aktualisiert',
        clearingCreated: ({ modifications: { from, to } }: ClearingCreatedActivity) =>
          from && to
            ? `Abrechnung vom ${formatDate(from) ?? '?'} bis ${formatDate(to) ?? '?'} angelegt`
            : 'Abrechnung angelegt',
        externalValidationCodeImport: ({
          modifications: { duplicates, datasets },
        }: {
          modifications: { duplicates: string[]; datasets: number };
        }) =>
          duplicates
            ? `Import der externen Codes war erfolgreich. Es wurden ${datasets} Datensätze importiert. ${duplicates.length} Codes wurden ignoriert, da sie bereits existieren.`
            : `Import der externen Codes war erfolgreich. Es wurden ${datasets} Datensätze importiert.`,
        productContractChanged: 'Vertrag geändert',
        contractAddedToProduct: 'Vertrag hinzugefügt',
        contractRemovedFromProduct: 'Vertrag entfernt',
        deliveryCreated: 'Lieferung erstellt',
        deliverySent: 'Lieferung versendet',
        deliveryDeleted: 'Lieferung gelöscht',
        invoiceCreated: 'Rechnung erstellt',
        invoiceBooked: 'Rechnung gebucht',
        invoiceDeleted: 'Rechnung gelöscht',
        itemsAddedToOrder: 'Produkte zur Bestellung hinzugefügt',
        orderPlaced: 'Bestellung platziert',
        orderAddedToPicklist: 'Bestellung zur Sammelliste hinzugefügt',
        itemsRefunded: 'Produkte storniert',
        paypalPaymentConfirmed: 'PayPal Zahlung bestätigt',
        paypalPaymentConfirmationFailed: 'PayPal Zahlung fehlgeschlagen',
        paypalWebhookProcessed: 'PayPal webhook processed',
        stripeSessionCompleted: 'Stripe-Zahlung bestätigt',
        stripeChargeFailed: 'Stripe charge failed',
        userChanged: 'Kunde aktualisiert',
        userRemoved: 'Kunde gelöscht',
        incomingItemsRemoved: 'Eingehende Produkte entfernt',
        outgoingItemsRemoved: 'Ausgehende Produkte entfernt',
        paymentAddedByAdmin: 'hat eine Zahlung hinzugefügt',
        preferredPaymentMethodUpdated: 'Präferierte Zahlart aktualisiert',
        markedProblematic: 'hat den Status “Problem” gesetzt',
        unmarkedProblematic: 'hat den Status “Problem” entfernt',
        autoCancelled: 'Auto-Storno',
        contingentChange: 'Kontingentsänderung',
        refundEmailSent: 'Rückerstattungsemail gesendet',
        itemsMassRefunded: 'Storniert mit Massenrückerstattungs-Tool',
        seatsBlocked: 'Sitze gesperrt',
        seatsUnblocked: 'Sitze freigegeben',
        saleStarted: 'Verkauf gestartet',
        saleStopped: 'Verkauf gestoppt',
        unarchived: 'Unarchiviert',
        applyVoucherCode: 'Gutscheincode angewendet',
        removeVoucherCode: 'Gutscheincode entfernt',
        userAddedToCompany: ({ modifications: { userName, email, role } }: UserAddedToCompanyActivity) =>
          `hat ${userNameAndOrEmail(userName, email)} als ${I18n.translate.model.role[role]} zum Team hinzugefügt`,
        userRemovedFromCompany: ({ modifications: { userName, email } }: UserRemovedFromCompanyActivity) =>
          `hat ${userNameAndOrEmail(userName, email)} aus dem Team entfernt`,
        membershipChanged: ({ modifications: { userName, email, role } }: MembershipChangedEvent) =>
          role
            ? `hat die Rolle von ${userNameAndOrEmail(userName, email)} zu ${
                I18n.translate.model.membership[role]
              } geändert`
            : `nutzerrechte von ${userNameAndOrEmail(userName, email)} geändert`,
        changeEventVenueLayout: 'Saalplan ausgetauscht',
        fixClearingRefunds: 'Automatische Abrechnungskorrektur für einige Erstattungen',
        deliveryConfirmationSent: 'Versandbestätigung gesendet',
        venueLayoutCreated: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Saalplan „${name}“ wurde erstellt`,
        venueLayoutImportDone: 'Saalplan erfolgreich importiert',
        destroyVenueLayout: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Saalplan „${name}“ wurde gelöscht`,
        updateVenueLayout: 'Saalplan wurde bearbeitet',
        venueLayoutImportFailedNoChart:
          'Saalplanimport ist fehlgeschlagen, weil kein Saalplan mit der ID gefunden wurde',
        unbookInvoice: 'Buchung revidiert',
        eventSaleStarted: ({ createdAt, modifications: { publishedFrom, salesStats } }: EventSaleChangeActivity) =>
          publishedFrom && publishedFrom > createdAt
            ? `hat den Verkauf für ${formatDate(publishedFrom)} geplant.`
            : `hat den Verkauf gestartet. Zu diesem Zeitpunkt waren ${salesStats.systemSold} Tickets verkauft.`,
        eventSaleRequested: ({ createdAt, modifications: { publishedFrom, salesStats } }: EventSaleChangeActivity) =>
          publishedFrom && publishedFrom > createdAt
            ? `hat den Verkauf für ${formatDate(publishedFrom)} beantragt.`
            : `hat den Verkauf beantragt. Zu diesem Zeitpunkt waren ${salesStats.systemSold} Tickets verkauft.`,
        eventSaleStopped: ({ modifications: { salesStats } }: EventSaleChangeActivity) =>
          `hat den Verkauf gestoppt. Zu diesem Zeitpunkt waren ${salesStats.systemSold} Tickets verkauft.`,
        inviteUser: ({ modifications: { invitedEmail, role } }: InviteUserActivity) =>
          `hat ${invitedEmail} als ${I18n.translate.model.role[role]} zum Team eingeladen`,
        createPricingCategory: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Preiskategorie „${name}” wurde erstellt`,
        destroyPricingCategory: ({ modifications: { name } }: { modifications: { name: string } }) =>
          `Preiskategorie „${name}” wurde gelöscht`,
        updatePassbookConfig: ({ modifications: { enabled } }: { modifications: { enabled: boolean } }) =>
          enabled ? 'Wallet-Ticket Ausstellung aktiviert' : 'Wallet-Ticket Ausstellung deaktiviert',
      },
      customCommentActivity: {
        seatsBlocked: (reason: string) => `Sitze gesperrt mit Begründung „${reason}“`,
        seatsUnblocked: 'Sitze freigegeben',
      },
      shipmentRelevant: 'Relevant für Versand',
      markAsShipmentRelevant: 'Relevant für Versand',
      productImportant: 'Wichtig für das Produkt',
      markAsProductImportant: 'Wichtig für das Produkt',
      comments: 'Notizen / Kommentare',
      comment: 'Kommentar',
      errors: {
        noEvent: 'Event nicht gefunden',
        noProduct: 'Produkt nicht gefunden',
        noEventOrProduct: 'Event oder Produkt nicht gefunden',
      },
      contingentChangeMessage: ({ from, to, delta }: { from: number; to: number; delta: number }) =>
        `von ${from} zu ${to} (Differenz ${delta})`,
      preferredPaymentMethodUpdate: ({ from, to }: { from: string; to: string }) => `von ${from} zu ${to}`,
      blockedSeats: {
        allSeats: 'Alle Sitzplätze',
        row: 'Reihe',
        seats: 'Sitzplätze',
      },
      processPaymentAuthorization: {
        capture: 'Zahlung mit Autorisierung eingezogen',
        release: 'Zahlungsautorisierung freigegeben',
      },
    },
    address: {
      _name: 'Addresse',
      additionalStreet: 'Zusätzliche Adressinformation',
      capacity: 'Kapazität',
      city: 'Stadt',
      country: 'Land',
      postCode: 'PLZ',
      street: 'Straße und Hausnummer',
    },
    allocation: {
      _name: 'Kontingent',
      _plural: 'Kontingente',
      allocationList: 'Kontingentliste',
      csv: 'Kontingent CSV',
      downloadPdf: 'Download Kontingent PDF',
    },
    artist: {
      _name: 'Artist',
      _plural: 'Artists',
    },
    bank: {
      _name: 'Bank',
      amount: 'Betrag',
      bankDetails: 'Bankverbindung',
      bankName: 'Bankname',
      bankTransfer: 'Überweisung',
      bic: 'BIC',
      cod: 'Nachnahme',
      paypal: 'PayPal',
      paypalExpress: 'PayPal Express',
      stripeExpress: 'ApplePay / GPay',
      amazon: 'Amazon',
      shopify: 'Shopify',
      recipient: 'Kontoinhaber',
      stripeCard: 'Kreditkarte',
      stripeSofort: 'Sofortüberweisung',
      stripeCheckout: 'Stripe Checkout',
      holdersName: 'Kontoinhaber',
      iban: 'IBAN',
      taxNumber: 'Steuernummer',
      vat: 'USt ID',
      vatId: 'EU VAT ID',
    },
    bookkeeping: {
      _name: 'Buchhaltung',
      bookKeepingExport: 'Buchhaltungsexport',
    },
    brand: {
      _name: 'Brand',
      _plural: 'Brands',
    },
    label: {
      _name: 'Label',
      _plural: 'Labels',
    },
    category: {
      _name: 'Kategorie',
      _plural: 'Kategorien',
      abstract: 'Zusammenfassung',
      altNames: 'Alternative Namen',
      altNamesDesc: 'Alternative Namen, durch Komma getrennt',
      featuredAt: 'Feature auf',
      mainCategory: 'Hauptkategorie',
      sortOrderFeatureBottom: 'Sortierung in der Featured-Sektion / unten',
      sortOrderFeatureTopLeft: 'Sortierung in der Featured-Sektion / oben links',
      sortOrderFeatureTopRight: 'Sortierung in der Featured-Sektion / oben rechts',
      orderTooltip:
        'Die Position der Kategorie in einer Liste von Kategorien. Zum Beispiel auf einer Bandshop-Hauptseite.',
      orderHomepageTooltip:
        'Die Position der Kategorie in der Featured-Sektion der Kategorien auf der Startseite des Hauptshops. Höhere Zahlen werden zuerst platziert. Leer lassen oder auf -1 setzen, um sie auszublenden.',
      orderTicketTopLeftTooltip:
        'Die Position der Kategorie im oberen linken Bereich des Hauptshops, je nach Art der Kategorie, entweder in / Merch, / Tickets oder / Artists. Höhere Zahlen werden zuerst platziert. Leer lassen oder auf -1 setzen, um sie auszublenden.',
      orderTicketTopRightTooltip:
        'Die Position der Kategorie im oberen rechten Bereich im Hauptshops, entweder in / Merch, / Tickets oder / Künstler, je nach Art der Kategorie. Höhere Zahlen werden zuerst platziert. Leer lassen oder auf -1 setzen, um sie auszublenden.',
      orderTicketBottomTooltip:
        'Die Position der Kategorie im unteren Bereich des Hauptshops, entweder in / Merch, / Tickets oder / Artists, je nach Art der Kategorie. Höhere Zahlen werden zuerst platziert. Leer lassen oder auf -1 setzen, um sie auszublenden.',
      slug: 'Slug',
      slugFormat:
        'Darf nur Buchstaben und Zahlen enthalten und ggf. mit einem Bindestrich getrennt werden. Beispiel: coole-kategorie',
      sort: 'Sort',
      loadingCategory: 'Kategorie wird geladen',
      shopFromParentCategory: 'Ist aus der Eltern-Kategorie festgelegt',
      meta: 'Meta',
    },
    charts: {
      _name: 'Charts',
      reports: 'Meldungen',
      date: 'Datum',
      fileName: 'Dateiname',
      download: 'herunterladen',
      upload: 'hochladen',
      uploadChart: 'Chart Export hochladen',
      uploadSuccess: 'Erfolgreich hochgeladen',
      uploadSuccessful: 'Die Datei wurde erfolgreich hochgeladen.',
      uploadFailed: 'Upload fehlgeschlagen',
      uploadFailedMessage: 'Hochladen der Datei nicht möglich.',
      preview: 'Vorschau',
      generatePreview: 'Vorschau generieren',
      manualUploaded: 'manuell',
      markAsResolved: 'Als gelöst markieren',
      confirmResolvedTitle: 'Fehler als gelöst markieren',
      confirmResolved: 'Bist Du sicher, dass Du den Fehler als gelöst markieren möchtest?',
      confirm: 'Bestätigen',
    },
    company: {
      _name: 'Firma',
      _plural: 'Firmen',
      accountsPaymentsTaxes: 'Konten, Zahlungsdetails und Steuerinformationen',
      billingAddress: 'Rechnungsadresse',
      billingContact: 'Abrechnungskontakt',
      billingInfo: 'Abrechnungsinformationen können nur durch einen Krasser Stoff Admin geändert werden.',
      currentCompany: 'Aktuelle Firma',
      invoices: 'Wir werden diese Adresse für alle Dokumente nutzen, wie z.B. Rechnungen.',
      noBankDetails: 'Keine Bankverbindung hinterlegt.',
      noRegisteredAddress: 'Keine Anschrift hinterlegt.',
      vendor: 'Lieferant',
      vendorInformation: 'Lieferanteninformation',
      vendors: 'Lieferanten',
      removeCompany: 'Firma entfernen',
      confirmRemoveCompany: 'Bist du dir sicher, dass du diese Firma von der Veranstaltung entfernen möchtest?',
      untitledCompany: 'Untitled Company',
    },
    contract: {
      _name: 'Vertrag',
      _plural: 'Verträge',
      contractLine: 'Vertragszeile',
      postTax: 'Post tax',
      receiver: 'Empfänger',
      vatOverride: 'VAT override',
      notAuthorized: 'Keine Ansichtsberechtigung für den Vertrag',
    },
    datetime: {
      _name: 'Datum',
      _plural: 'Daten',
      activeDate: 'Aktiv ab',
      createdAt: 'Erstellt am',
      datesAndTimes: 'Daten und Zeiten',
      endDate: 'Enddatum',
      eternity: 'unendlich',
      formatHHMM: 'Format: HH:MM',
      invalidatedAt: 'Invalidiert am',
      publishDate: 'Sichtbar ab',
      publishDateTime: 'Sichtbar Datum / Zeit',
      unPublishDate: 'Sichtbar bis',
      startDate: 'Startdatum',
      timeframe: 'Zeitraum',
      updatedAt: 'Bearbeitet am',
      week: 'Woche',
      duration: 'Event Länge',
    },
    chartExportTypes: {
      charts: 'Musik Charts',
      bookCharts: 'Bücher Charts',
    },
    countries: {
      de: 'Deutschland',
      at: 'Österreich',
      ch: 'Schweiz',
    },
    delivery: {
      _name: 'Lieferschein',
      _plural: 'Lieferscheine',
      carrier: 'Dienstleister',
      delivery: 'Lieferung',
      incoming: 'eingehend',
      outgoing: 'ausgehend',
      scanDeliveryOrReference: 'Lieferschein scannen oder Referenz eingeben',
      deliveryNoteType: 'Lieferscheintyp',
      deliveryNoteTypes: {
        inventory_correction: 'Bestandskorrektur',
        damage: 'Schwund/Defekt',
        new_goods: 'Neuware',
        circulating_goods: 'Umlaufware',
        other: 'Sonstiges',
      },
      trackingCode: 'Tracking Code',
      editTrackingCodeAndType: 'Tracking Code und Typ bearbeiten',
      trackingLink: 'Tracking Link',
      deleteDelivery: 'Lieferung löschen',
      shipping: 'Versand',
      shippingMethod: 'Versandart',
      deliveryPlaced: 'Lieferschein platziert',
      deliveryBooked: 'Lieferschein gebucht',
      no: 'Kein Lieferschein',
      new: 'Neuer Lieferschein',
      removeFromPicklist: 'Von Sammelliste entfernen',
      removedFromPicklist: 'Lieferschein wurde von Sammelliste entfernt',
      confirmRemoveFromPicklist:
        'Bist du dir sicher, dass du diesen Lieferschein von der Sammelliste entfernen möchtest?',
    },
    event: {
      _name: 'Veranstaltung',
      _plural: 'Veranstaltungen',
      admission: 'Einlass',
      admissionStatisticsLink: 'Teilbare Einlassstatistik öffnen',
      announcementOn: 'Ankündigung am',
      billing: 'Abrechnung',
      billingItem: 'Abrechnung',
      blockAllSeats: 'Alle Sitzplätze am Anfang sperren?',
      changeDueToActiveResale:
        'Achtung: Ticketcodes können sich noch ändern, da der re:sale aktiviert ist. Wende dich an uns, damit wir den re:sale beenden können oder rechne mit "unbekannten" Tickets am Einlass.',
      changeDueToProcessing: (count: number) =>
        `Achtung: Ticketcodes können sich noch ändern, da noch ${count} ${pluralize(
          count,
          'Ticket verarbeitet wird',
          'Tickets verarbeitet werden'
        )}. Wenn du die Liste in 10-15min erneut lädst, sollten alle Ticketcodes enthalten sein.`,
      changeDueToCart: (count: number) =>
        `Ticketcodes können sich noch ändern, da sich noch ${count} ${pluralize(
          count,
          'Ticket in einem Warenkorb befindet',
          'Tickets in Warenkörben befinden'
        )}. Wenn du die Liste in 10-15min erneut lädst, sollten alle Ticketcodes enthalten sein.`,
      changeDueToReserved: (count: number) =>
        `Ticketcodes können sich noch ändern, da noch ${count} ${pluralize(
          count,
          'Ticket reserviert ist. Wenn dieses Ticket noch gebucht wird, wird es ein gültiges Ticket, es ist jedoch nicht in der Code Liste enthalten.',
          'Tickets reserviert sind. Wenn diese Tickets noch gebucht werden, werden sie gültige Tickets. Die Codes sind jedoch nicht in der Code Liste enthalten'
        )}`,
      ticketcodes: 'Ticketcodes',
      changeOwner: 'Veranstalter ändern',
      chartsKey: 'Chart Key / URL zu JSON',
      chartsScale: 'Planskalierung',
      chartsScaleDescription: 'Der importierte Saalplan wird um diesen Faktor skaliert.',
      close: 'Schließen',
      confirmRequestMsg:
        'Hiermit bestätigst du, dass du der Veranstalter bist dieser Veranstaltung bist. Du beauftragst die Krasser Stoff Merchandising GmbH mit dem Verkauf der Karten zu dieser Veranstaltung. Wir werden die Veranstaltung nach dem beantragten Verkauf prüfen, zum gesetzten Zeitpunkt veröffentlichen und zum Verkaufsstart mit dem Verkauf beginnen. Möchtest du fortfahren?',
      doorTime: 'Einlass',
      draftMsg: 'Sobald du alle nötigen Daten eingegeben hast, kannst du hier den Verkauf beantragen.',
      deleteEventTitle: 'Veranstaltung löschen',
      deleteEventConfirmation: 'Sind Sie sicher, dass Sie die Veranstaltung löschen möchten?',
      displayingTourImage: '* Bild der Tour',
      externalReference: 'Externe Referenz',
      eventId: 'Event ID',
      eventTitle: 'Event Titel',
      excludeFromFeeds: 'Von Feeds ausschließen (Facebook & Google)',
      externalCodesUpload: 'Externe Codes hochladen',
      externalCodesUploadDescription:
        'Hier kannst du eine CSV-Datei mit externen Ticketcodes hochladen. Diese Ticketcodes können dann auch mit der Krasser Stoff Scan-APP gescannt werden. Achte darauf, dass deine Datei immer nur einen Code pro Zeile enthält und keine extra Kopfzeile.',
      externalCodesUploadSuccessModal: 'Upload externer Codes',
      externalCodesUploadSuccessModalDescription: (duplicates: string[]) =>
        `Der Import war erfolgreich. Es wurden ${duplicates.length} bereits existierende Codes gefunden: `,
      happeningOn: 'Die Veranstaltung findet statt am',
      includePublicTransportInfo: 'ÖPNV-Informationen anzeigen',
      inReviewMsg:
        'Vielen Dank. Wir überprüfen nun die Angaben zum Event. Unsere Bürozeiten sind Mo - Fr, 10:00-17:00h.',
      instantPaymentLabel: 'Schwelle für Instant-Zahlarten',
      instantPaymentDescription: {
        only: 'nur Sofortzahlungen erlaubt',
        all: 'alle Zahlarten sind erlaubt',
        custom: 'andere Einstellung auf Basis der Verfügbarkeit',
      },
      instantPaymentCustomDescription:
        '0% bedeutet, dass nur Sofortzahlungen erlaubt sind\nz.B. 50% bedeutet, dass nach 50% der verkauften Tickets nur noch Sofortzahlungen erlaubt sind\n100% bedeutet, dass alle Zahlungsarten erlaubt sind, bis die Veranstaltung ausverkauft ist.',
      localOperator: 'Lokaler Veranstalter',
      publishedUntillWarning: (publishedUntilLabel: string) =>
        `Achtung: <strong>${publishedUntilLabel}</strong> weicht vom Standard-Wert ab`,
      updatePublishedUntil: (publishedUntilLabel: string, defaultPublishedUntilString: string) =>
        `Verwende ${defaultPublishedUntilString} für ${publishedUntilLabel}`,
      massRefund: {
        _name: 'Massenrückerstattung',
        massRefundable: 'Massenrückerstattbar?',
        autoBookRefunds: 'Massenrückerstattungen automatisch buchen?',
        usedTicketsMassRefundable: 'Massenrückerstattungen erlauben für verwendete Tickets?',
        emailTemplateDe: 'Email Template DE',
        emailTemplateEn: 'Email Template EN',
        emailSubjectDe: 'Email Betreff DE',
        emailSubjectEn: 'Email Betreff EN',
        customTitleDe: 'Titel Massenrückerstattung DE',
        customTitleEn: 'Titel Massenrückerstattung EN',
        refundExplanationDe: 'Text Titel Massenrückerstattung DE',
        refundExplanationEn: 'Text Titel Massenrückerstattung EN',
        triggerMassRefund: 'Massenrückerstattung starten',
        pauseMassRefund: 'Massenrückerstattung pausieren?',
        sendEmailTo: 'Bist du dir sicher dass du eine Email an',
        peopleNum: (num: number) => `${num} People`,
        withInstructions: 'mit der Aufforderung zur Rückabwicklung senden möchtest?',
        ticketRefundTool: 'Ticket Rückerstattung',
        linkToRefundPage: 'Link zur Ticketrückererstattung',
        sendEmailWithTemplate: 'Email mit Template senden',
        sendRefundEmail: 'Email senden',
        refundTriggeredAt: 'Rückerstattungsemail gesendet am',
        refundPaused: 'Rückerstattungen sind pausiert',
        refundSuccessful: 'Rückerstattung erfolgreich',
      },
      meta: 'Meta',
      noPaymentThresholdSet: 'Es wurde keine Schwelle für Instant-Zahlarten gesetzt',
      organizer: 'Organisator',
      owner: 'Veranstalter',
      paymentOptions: 'Zahlungs Optionen',
      paymentThreshold: 'Schwelle für Instant-Zahlarten',
      personalization: {
        _name: 'Personalisierung',
        noPersonalizationRequired: 'Keine Personalisierung',
        groupPersonalizationRequired: 'Gruppen-Personalisierung (alle Tickets gleicher Name)',
        ticketPersonalizationRequired: 'Einzel-Personalisierung (ein Name per Ticket)',
        contactTracingRequired: 'Contact-Tracing',
        checkInRequired: 'Check-In',
        bdkv: 'BDKV Personalisierung',
      },
      productVariantsActiveFromWarning: 'Dies überschreibt alle Daten, ab denen die Produktvarianten aktiv sind.',
      promoter: 'Veranstalter',
      publicTransportDescription: 'ÖPNV-Informationen',
      publicTransportDescriptionDefault:
        'Der Ticketpreis beinhaltet ein ÖPNV Ticket. Die Eintrittskarte gilt jedoch nicht als Fahrschein. Bitte scanne den QR-Code, um deinen Fahrschein zu erhalten oder gehe auf https://krasserstoff.com/opnv.',
      publishAt: 'Sichtbar ab',
      resale: {
        allow: 'Erlaubt',
        confirm: 'Bestätigen',
        editSettings: 'Einstellungen anpassen',
        info: 're:sale Einstellungen',
        off: 'Aus',
        onSoldOut: 'Nur wenn die Veranstaltung ausverkauft ist',
        privateLink: 'Privaten Link teilen',
        publicOffer: 'Öffentliches Angebot',
        resalePreference: 're:sale erlauben',
        tooltip: 'Die re:sale-Funktion erlaubt oder verbietet es Fans, ihre gekauften Tickets weiterzuverkaufen.',
        visibilitySettings: 'Sichtbarkeit',
        visibilities: {
          privateLink: 'Privater Link',
          publicOffer: 'Öffentliches Angebot',
        },
        visibilitySettingsRequired: 'Mindestens eine Option muss angewählt werden.',
        preferences: {
          allow: 'Erlauben',
          onSoldOut: 'Wenn die regulären Tickets ausverkauft sind.',
          off: 'Aus',
        },
        offer: {
          cardHeader: 'Tickets',
          orderReference: 'Bestellungsreferenz',
          onOfferSince: 'Im Angebot seit',
          validationCode: 'Ticketcode',
          status: 'Status',
          visibility: 'Sichtbarkeit',
          privateLink: 'Privater Link',
          publicOffer: 'Öffentliches Angebot',
          resaleOffer: 'Wiederverkaufsangebot',
          purchased: 'gekauft',
          cart: 'Warenkorb',
          available: 'verfügbar',
        },
        salesOverTime: 'Verkäufe im Laufe der Zeit',
      },
      sendRequest: 'Anfrage senden',
      shopContent: 'Shop/Content',
      shopSettings: 'Shop Einstellungen',
      ShopCategories: 'Shop/Tour/Künstler',
      ShopCategoriesModalTitle: 'Shop/Tour/Künstler Zuordnung',
      noCategoryAssociation: 'Keine Zuordnungen',
      shopTours: 'Shops/Touren',
      showArchived: 'Archivierte Saalpläne anzeigen',
      sku: 'SKU',
      slug: 'Slug',
      slugFormat:
        'Darf nur Buchstaben und Zahlen enthalten und ggf. mit einem Bindestrich getrennt werden. Beispiel: coole-veranstaltung',
      soldOutMsg: 'Sold out Mitteilung',
      stageTime: 'Beginn',
      ticketCodesButton: 'Download Ticketcodes',
      fullTicketCodesButton: 'Download Ticketcodes with meta',
      ticketCodesMsg: 'Du musst den Verkauf beenden, um Ticketcodes herunterzuladen.',
      tourOperator: 'Tourveranstalter',
      updateSoldOutMsg: 'Sold Out Mitteilung ändern',
      updateOwningCompany: 'Owner ändern',
      eventDetails: 'Event Details',
      stats: 'Statistiken',
      untitledEvent: 'Untitled Event',
      utmTrackingCampaigns: {
        title: 'UTM Tracking Campaigns',
        tooltip: 'Verfolge die Top 10 Kampagnen für dieses Event',
      },
      utmTrackingSources: {
        title: 'UTM Tracking Sources',
        tooltip: 'Verfolge die Top 10 Quellen für dieses Event',
      },
      utmTrackingMedia: {
        title: 'UTM Tracking Media',
        tooltip: 'Verfolge die Top 10 Medien für dieses Event',
      },
      noSeatsAvailable: 'Keine Sitzplätze verfügbar',
      ticketCodeUsageUpload: 'Verwendete Ticketcodes hochladen',
      ticketCodeUsageUploadDescription:
        'Hier können Sie eine CSV-Datei mit bereits verwendeten Ticketscodes hochladen. Die Datei muss dabei folgendes Format haben (ein Code pro Zeile): Code; Zeitstempel (optional); Scanner (z.B. "Eingang A, Scanner 2", optional).',
      importantCommentsTitle: 'WARNUNG: Wichtige Information für diese Veranstaltung',
      scannerCodeTypes: 'Scanner Code Types',
      noScannerCodeTypes: 'No scanner code types allowed for this event.',
      _codeTypes: {
        qr: {
          label: 'QR',
          description: '2d QR Code',
        },
        ean: {
          label: 'EAN',
          description: '1d EAN Barcode',
        },
      },
    },
    image: {
      _name: 'Bild',
      _plural: 'Bilder',
      imageUrl: 'Bild URL',
      square: 'Bild „Quadratisch“',
      landscape: 'Bild „Querformat“',
      portrait: 'Bild „Hochformat“',
    },
    invoice: {
      _name: 'Rechnung',
      _plural: 'Rechnungen',
      bookedAt: 'Gebucht am',
      bookingNumber: 'Buchungsnummer',
      billingEmails: 'E-Mail-Adressen für Abrechnungen',
      customerData: 'Kundendaten',
      clearingEmailSubject: (invoice: string) => `Abrechnung ${invoice}`,
      clearingEmailBody: (link: string) =>
        `Wir haben eine neue Abrechnung erstellt. Diese kann jetzt unter dem folgenden Link heruntergeladen werden. Der Link ist möglicherweise nicht unbegrenzt gültig. Daher empfehlen wir, die Abrechnung herunterzuladen und lokal zu archivieren.\n${link}`,
      invoicePlaced: 'Rechnung platziert',
      invoiceBooked: 'Rechnung gebucht',
      deleteInvoice: 'Rechnung löschen',
      unbookInvoice: 'Buchung revidieren',
      unbookInvoiceMessage:
        'Bitte bestätige, dass du diese Buchung revidieren und die Items wieder für eine neue Abrechnung freigeben möchtest. Die Buchungsnummer wird bei erneuter Buchung nicht wiederverwendet.',
      unbookInvoiceWarning:
        'ACHTUNG: Bei Items mit 100% Auszahlung und Rechnung (z.B. Veranstaltungsitems) müssen beide Rechnungen gelöscht werden, da es sonst zu Doppelfakturierungen kommen kann.',
      new: 'Neue Rechnung',
      no: 'Keine Rechnung',
      downloadDetails: 'Details herunterladen',
      invoicing: 'Abrechnung',
      invoicingCycle: 'Merch Abrechnungszyklus',
      eventPayoutCycle: 'Event Abrechnungszyklus',
      lastEventPayout: 'Letzte Event Auszahlung',
      defaultEventPayoutPercentage: 'Event Auszahlungsatz %',
      invoicingCycles: {
        none: 'keine Abrechnung',
        weekly: 'wöchentlich',
        monthly: 'monatlich',
        quarterly: 'quartalsweise',
      },
      lastClearingInvoice: 'Letzte Merch Abrechnung',
      previousInvoicePdf: 'Link zum PDF',
    },
    item: {
      _name: 'Produkt',
      _plural: 'Produkte',
      itemCount: (count: number) => `${count} ${pluralize(count, 'Produkt', 'Produkte')}`,
      createdAt: 'Hinzugefügt am',
      price: 'Preis',
      quantity: 'Anzahl',
      unknownItem: 'Unknown Item',
      vatPercentage: 'MwSt %',
      validationCode: 'Ticketcode',
      usedAt: 'benutzt am',
      removeModal: {
        removeAndRelease: 'entfernen und freigeben',
        removeAndBlock: 'entfernen und sperren',
        ticketOptions: (tickets: number) => `Entfernungsoptionen ${pluralize(tickets, 'Ticket')}`,
        mixedWarning: (tickets: number) =>
          `Du entfernst eine Auswahl aus Produkten und ${pluralize(tickets, 'Ticket')}.`,
      },
    },
    logo: {
      _name: 'Logo',
      _plural: 'Logos',
      presenterLogo: 'Präsentatoren Logo',
    },
    membership: {
      _name: 'Mitarbeiter',
      _plural: 'Mitarbeiter',
      editor: 'Editor',
      observer: 'Betrachter',
      owner: 'Eigentümer',
      role: 'Rolle',
      merchAdmin: 'Merch Admin',
      financialAdmin: 'Finanzen',
      financialAdminDescription: 'Die Finanzen-Berechtigung ermöglicht Zugriff auf die Statistiken des Dashbaoards.',
      ticketingAdmin: 'Ticketing Admin',
      admin: 'Admin',
      copyToken: 'API Token in die Zwischenablage kopieren',
      copyConfirmation: 'Der API Token wurde in die Zwischenablage kopiert.',
      regenerateToken: 'API Token generieren',
      regenerateTokenConfirmation:
        'Dadurch wird der aktuelle Token ungültig und alle verbunden APIs werden getrennt. Bist du sicher, dass du fortfahren möchtest?',
      copyCalendarFeedUrlToClipboard: 'Kalender Subscription URL in Zwischenablage kopieren',
      copyCalendarFeedUrlToClipboardConfirmation: 'Kalender Subscription URL in Zwischenablage kopiert.',
      inviteNewUser: 'Neue Mitarbeitende einladen',
      addRegisteredUser: 'Einen registrierten Mitarbeitenden hinzufügen',
      editRegisteredUser: 'Einen registrierten Mitarbeitenden bearbeiten',
      editInvitation: 'Einladung bearbeiten',
      sendInvite: 'Einladen',
      invitedExpires: (createdAt?: string, expiresAt?: string) => {
        return `(${[
          createdAt && `Eingeladen am ${formatDate(createdAt)}`,
          expiresAt && `Gültig bis ${formatDate(expiresAt)}`,
        ]
          .filter(Boolean)
          .join('. ')})`;
      },
      invitationExpired: 'Einladung Abgelaufen',
      awaitingResponse: 'Einladung ausstehend',
      dateOfInvite: 'Eingeladen am',
      linkExpiration: 'Einladung gültig bis',
      noLongerAMember: 'Du hast leider keinen Zugriff mehr auf diese Seite, da du kein Teammitglied bist.',
      invalidLink: 'Der Einladungslink ist nicht mehr gültig. Du benötigst eine neue Einladung',
      expiredLink: (invitedBy: string) =>
        `Der Einladungslink ist abgelaufen. Bitte kontaktiere ${invitedBy} oder ein anderes Mitglied, um erneut eingeladen zu werden.`,
      invitedEmailMismatch: (invitedEmail: string, currentUserEmail: string) =>
        `Diese Einladung wurde an ${invitedEmail} gesendet, aber du bist mit ${currentUserEmail} eingeloggt.`,
      membershipChanged: {
        title: 'Firma gewechselt',
        message: 'Die Seite wurde aktualisiert da ein Firmenwechsel in einem anderen Tab erfolgt ist',
      },
    },
    order: {
      _name: 'Bestellung',
      _plural: 'Bestellungen',
      autoCancelAt: 'Auto-Storno',
      balance: 'Saldo',
      deliveries: 'Lieferungen',
      exportFulfillmentLists: 'Sammelliste exportieren',
      incomingProducts: 'Wareneingang',
      items: ':count: Produkte',
      invoiceAddress: 'Rechnungsadresse',
      shippingAddress: 'Versandadresse',
      massUpdateTags: 'Tags hinzufügen & ändern',
      noItems: 'Diese Bestellung hat keine Produkte.',
      orderId: 'Order ID',
      selectedOrder: 'ausgewählte Bestellung',
      selectedOrders: 'ausgewählte Bestellungen',
      startClearingCycle: 'Abrechnung starten',
      stocking: 'Einlagerungs Beleg',
      storageLocation: 'Lagerplatz',
      noStorageLocation: 'Kein Lagerplatz',
      tags: 'Tags',
      ticketList: 'Ticket Liste',
      user: 'Kunde',
      addUserToOrder: 'Kunde ändern',
      withShippableProductsOnly: 'nur versandfertige Produkte',
      includeArchive: 'Archiv einbeziehen',
      exportEmails: 'Email-Adressen exportieren',
      setProblem: 'Problem Status setzen',
      setProblemMsg: 'Bist du dir sicher dass du die Bestellung auf den Status “Problem” setzen möchtest?',
      unsetProblem: 'Problem Status entfernen',
      unsetProblemMsg: 'Bist du dir sicher dass du den Problem Status von der Bestellung entfernen möchtest?',
      createAnother: 'weiteres Produkte hinzufügen',
      editCancelAt: 'Automatischer Stornierungszeitpunkt',
      deleteOrderTitle: 'Bestellung löschen',
      deleteOrderConfirmation: 'Bist du sicher, dass du diese Bestellung löschen möchtest?',
      orderDeleted: 'Bestellung gelöscht',
      importantInformationAboutProducts: `Wichtige Information für das Produkt: `,
      copyPaymentLink: 'Zahlungslink in die Zwischenablage kopieren',
      copyPaymentLinkConfirmation: 'Der Zahlungslink wurde in die Zwischenablage kopiert.',
      editSuccess: 'Die Zahlung wurde erfolgreich bearbeitet',
      editFailed: 'Beim Bearbeiten der Zahlung ist ein Fehler aufgetreten',
      containingOnly: 'Enthält...',
      resendOnlineTickets: 'Online-Tickets erneut senden',
      resendConfirmationMail: 'Bestellbestätigung erneut senden',
      onlyContainingOptions: {
        onlineTicketOnly: 'Nur Online-Ticket',
        hardTicketOnly: 'Nur Hardticket',
        merchOnly: 'Nur Merch',
        hardTicketAndMerch: 'Hardticket und Merch',
      },
      shopDetails: {
        title: 'Shop Details',
        shop: 'Shop',
        domain: 'Domain',
        update: 'Shop ändern',
        noShop: 'Diese Bestellung gehört zu keinem Shop.',
        addedSuccessfully: 'Shop erfolgreich hinzugefügt',
        removedSuccessfully: 'Shop erfolgreich entfernt',
      },
      voucherDetails: {
        title: 'Gutschein Details',
        campaign: 'Gutschein Kampagne',
        code: 'Gutschein Code',
        discount: 'Rabatt',
        enterCode: 'Gutschein Code eingeben',
        addedSuccessfully: 'Gutschein erfolgreich hinzugefügt',
        removedSuccessfully: 'Gutschein erfolgreich entfernt',
        voucherCodeApplied: (code: string) => `Der Gutscheincode ${code} wird eventuell angewendet.`,
      },
      showCount: 'Anzahl anzeigen',
      close: 'Schließen',
      ordersCount: 'Anzahl der Bestellungen',
      capturePayment: {
        title: 'Zahlung abbuchen',
        description: 'Sind Sie sicher, dass Sie die Zahlung für diese Bestellung abbuchen möchten?',
        confirm: 'Bestätigen',
      },
      releasePayment: {
        title: 'Zahlung freigeben',
        description: 'Sind Sie sicher, dass Sie die Zahlung für diese Bestellung freigeben möchten?',
        confirm: 'Bestätigen',
      },
      offerForResale: {
        title: 'Zum Weiterverkauf anbieten',
        public: 'Öffentlicher Weiterverkauf',
        bankDetails: 'Bankdaten',
        recipient: 'Empfänger',
        iban: 'IBAN',
        bic: 'BIC',
        bankName: 'Bankname',
        confirm: 'Bestätigen',
        successTicket: 'Das Ticket wurde erfolgreich zum Weiterverkauf angeboten.',
        successTickets: 'Die Tickets wurden erfolgreich zum Weiterverkauf angeboten.',
      },
    },
    backPayments: {
      _title: 'Rückzahlungen',
      downloadPending: (type: string) => `Download ${type} Liste`,
      alsoBook: 'Zahlungen im System buchen',
      confirmationTitle: 'Download & Zahlungen buchungen',
      bookingConfirmation: `Bist du dir sicher dass du alle ausgehenden, ausstehenden Zahlungen buchen möchtest?`,
    },
    payment: {
      _name: 'Zahlung',
      _plural: 'Zahlungen',
      paymentMethod: 'Zahlungsart',
      skipCustomerEmail: 'keine Email an Kunden senden',
      adminPayment: 'Admin Zahlung',
      markAsPaid: 'als bezahlt markieren',
      status: 'Status',
    },
    permissions: {
      setPermissionsForThisItem: (item: string) => `Berechtigungen für ${item}`,
      setPermissionsForThisShop: 'Berechtigungen für diesen Shop',
      deleteTitle: 'Berechtigung löschen',
      deleteConfirmation: 'Bist du sicher, dass du diese Berechtigung löschen möchtest?',
    },
    picklist: {
      _name: 'Sammelliste',
      _plural: 'Sammellisten',
      addresslistCSV: 'Adressenliste CSV',
      ticketlistPDF: 'Ticketliste PDF',
    },
    price: {
      _name: 'Preis',
      _plural: 'Preise',
      _verb: 'Preise',
      active: 'aktiv',
      inactive: 'inaktiv',
      allVariants: 'Alle Varianten',
      basePrice: 'Grundpreis',
      calculatePrice: 'Preis berechnen',
      calculationMode: 'MwSt Berechnungsmodus',
      currency: '(€ netto)',
      currencyWithVat: '(€ netto/brutto)',
      currencyGross: '(€ brutto)',
      grossPrice: 'Brutto Preis',
      brutPrice: 'Brutto Preis',
      grossPriceWithVAT: (vat) => `Brutto Preis (inkl. ${vat} MwSt.)`,
      editDetails: 'Details bearbeiten',
      netPrice: 'Netto Preis',
      presaleFee: 'VVK Gebühr',
      priceParts: 'Zusatzgebühr',
      systemFee: 'Systemgebühr',
      net: 'netto',
      gross: 'brutto',
      addNewPricepart: 'Preisbestandteil hinzufügen',
      total: 'Gesamt',
      multipleValues: 'Mehrere Steuersätze',
      refundDeduction: 'Einbehalt bei Rückgabe',
      vatRatesEU: 'USt Sätze für EU Länder',
      vatRateDE: 'USt Satz Deutschland',
      vatRateEU: 'USt Satz innerhalb EU',
      vatRateWorld: 'USt Satz Rest der Welt',
      vatRateMismatch:
        'Der USt Satz weicht vom USt Satz im Vertrag ab, Du kannst trotzdem fortfahren, wenn du dir sicher bist.',
      grossForGermany: 'Vorschau Bruttopreis für Deutschland',
      clickToExpand: 'Klicke auf das Icon um den USt Satz für EU Staaten zu bearbeiten',
      nonEU: 'Dieser USt Satz wird angewendet bei allen Ländern die mit den obigen Feldern nicht abgedeckt sind.',
      divergingRates: 'Abweichende Steuersätze',
      sameRates: 'Gleiche Steuersätze',
      presetRates: 'Vordefinierte Steuersätze anzeigen',
      customRate: 'Abweichenden USt Satz eingeben',
      vatRateMismatchModal: {
        title: 'Abweichender Steuersatz',
        confirmationPrompt: (value?: number) =>
          `Bist du dir sicher dass du den Steuersatz auf ${value || ''}% einstellen möchtest?`,
        description: 'Der Steuersatz weicht vom ausgewählten Vertrag ab.',
      },
      vat: 'MwSt',
      priceVariant: 'Preisvariante',
      originalPrice: 'alter/durchgestrichener Preis',
      originalPriceDescription:
        'Wenn du dieses Feld ausfüllst, wird das Produkt rabattiert im Shop angezeigt. Dieser Nettopreis (+USt) wird als durchgestrichener Preis angezeigt.',
      originalPriceBrut: 'alter/durchgestrichener Preis (Brutto)',
      comment: 'Kommentar',
      editTaxes: 'MwSt bearbeiten',
      createPrice: 'Preis erstellen',
      world: 'Weltweit',
      resaleBuyerFee: 're:sale Käufergebühr (19% MwSt)',
      resaleSellerFee: 're:sale Wiederverkaufsgebühr (19% MwSt)',
      defaultValue: 'Standardwert',
    },
    pricingCategory: {
      _name: 'Preiskategorie',
      _nameShort: 'Preiskategorie',
      _plural: 'Preiskategorien',
      name: 'Name',
      statistics: 'Kontingent per Preiskategorie',
      seatsCount: 'Sitze',
      venueSectionsCount: 'Bereiche',
      csv: 'Preiskategorie CSV',
    },
    product: {
      _name: 'Produkt',
      _plural: 'Produkte',
      addContent: 'Content hinzufügen',
      additionalInfo: 'Zusätzliche Information',
      additionalInfoBellow: 'Zusatz Info (Anzeige unter dem Produkt)',
      additionalInfoSide: 'Zusatz Info (Anzeige neben dem Produkt)',
      artistBrand: 'Artist / Brand',
      availableCount: 'Anzahl verfügbar',
      blockedCount: 'Anzahl gesperrt',
      bookedCount: 'Anzahl gebucht',
      bundleConfigExport: 'Bundle Config Export',
      contentDe: 'Beschreibung DE',
      contentEn: 'Beschreibung EN',
      contract: 'Vertrag',
      contractItemOwner: 'Vertrag / Item Owner',
      costAccount: 'Kostenstelle',
      deactivateAt: 'deaktivieren am',
      draftsCount: 'Drafts Count',
      editGeneral: 'Edit General',
      excludeFromFeeds: 'Ausschließen von Feeds (Facebook & Google)',
      general: 'Allgemein',
      googleFeedCategory: 'Google Feed Kategorie',
      googleFeedCategories: {
        clothing_and_accessoires: 'Bekleidung & Accessoires',
        magazines_and_newspapers: 'Zeitschriften & Zeitungen',
        music_and_sound_recordings: 'Musik & Tonaufnahmen',
        posters_prints_and_visual_artwork: 'Poster & Bildende Kunst',
        lighters_and_matches: 'Feuerzeuge & Streichhölzer',
        print_books: 'Gedruckte Bücher',
      },
      freeShippingMode: {
        title: 'Kostenloser Versand Modus',
        off: 'Kein kostenloser Versand, es sei denn ein anderes Produkt erzwingt ihn.',
        unanimous: 'Kostenloser Versand, falls er auch bei allen anderen Produkten der Bestellung aktiviert ist.',
        force: 'Kostenlosen Versand erzwingen, selbst wenn er nicht bei allen Produkten der Bestellung aktiviert ist.',
      },
      materialMissingWarning: 'Fehlende Angaben zum Material des Produktes',
      merch: 'Merch',
      merchandise: 'Merchandise',
      availability: 'Verfügbarkeit',
      minAvailability: 'Min. Verfügbarkeit',
      low: 'gering',
      noContent: 'Keine Beschreibung für diese Sprache vorhanden.',
      overlayDetail: 'Overlay Detail',
      overlayListing: 'Overlay Liste',
      owner: 'Eigentümer',
      productImageDescription:
        'Hier kannst du Bilder hochladen, die Reihenfolge per Drag&Drop ändern und eines oder in Spezialfällen mehrere als Listenbilder markieren.',
      productVariant: 'Produktvariante',
      productVariantActivation: 'Produktvariante aktivieren',
      publicShippableFrom: 'Kommuniziertes Versanddatum, wird im Shop angezeigt',
      size: 'Größe',
      sku: 'SKU',
      slug: 'Slug',
      slugFormat:
        'Darf nur Buchstaben und Zahlen enthalten und ggf. mit einem Bindestrich getrennt werden. Beispiel: cooles-produkt',
      sortOrder: 'Reihenfolge',
      stock: 'Bestand',
      variation: 'Variation',
      weight: 'Gewicht',
      untitledProduct: 'Untitled Product',
      noMusicChart: 'Es gibt keine Musik Charts Angaben zu diesem Produkt',
      noBookChart: 'Es gibt keine Buch Charts Angaben zu diesem Produkt',
      shippableFrom: 'Versandbearbeitung im Lager ab',
      fulfillment: 'Versand',
      dhlShippableFrom: 'DHL Label vordatieren und Versandbestätigung später senden (Experteneinstellung)',
      customsTariffNumber: 'Zolltarifnummer',
      customVat: 'abweichender Steuersatz',
      checkoutOptions: 'Checkout-Optionen',
      instantPaymentRate: 'Sofortige Zahlungsrate',
      cartLimit: 'Warenkorb-Limit',
      maximumItems: 'Maximale Anzahl von Artikeln im Warenkorb',
      cantClone: 'Dieses Produkt kann nicht geklont werden, da es ein kombiniertes Produkt ist.',
      partOfBundle:
        'Dieses Produkt ist Teil eines Pakets und muss an der Kasse mit anderen Produkten kombiniert werden.',
      loadingCombinedProducts: 'Laden kombinierter Produkte.',
      possibleCombinations: 'Mögliche Kombinationen:',
      thisProductAnd: 'Dieses Produkt UND',
      importantCommentsTitle: 'WARNUNG: Wichtige Information für das Produkt',
      startSale: 'Verkauf starten',
      stopSale: 'Verkauf beenden',
      altText: {
        title: 'Alternativtext',
        inListing: 'In Liste',
        inGallery: 'In Gallerie',
        update: 'Alternativtext aktualisieren',
        english: 'Englischer Alternativtext',
        german: 'Deutscher Alternativtext',
      },
      meta: 'Meta',
    },
    merchStatistics: {
      _name: 'Auswertungen',
      productVariants: 'Auswertungen nach Varianten',
      productStatus: 'Produktstatus',
      displayOnlyActive: 'Nur aktive Produktvarianten anzeigen',
      initialStock: {
        title: 'Anfangsbestand',
        short: 'Start',
        description: 'Warenbestand am Anfang der Periode.',
      },
      incomingStock: {
        title: 'Wareneingang',
        short: 'Eing.',
        description: 'Allgemeiner Wareneingang, i.d.R. Anlieferung von Neuware oder Anlieferung von anderen POS',
      },
      outgoingStock: {
        title: 'Warenausgang',
        short: 'Ausg.',
        description: 'Sonstiger Warenausgang ohne Berechnung',
      },
      lostStock: {
        title: 'Schwund',
        short: 'Schw.',
        description: 'Defekte, verschmutzte oder verlorene Produkte',
      },
      soldDelivered: {
        title: 'Verkauft und Geliefert',
        short: 'Verk.',
        description: 'In der Periode verkaufte und ausgelieferte Einheiten',
      },
      sentIncomplete: {
        title: 'Unvollständig/Sonstige',
        short: 'Unvoll.',
        description: 'Unklarer Produktstatus, in Klärung durch Kundenservice',
      },
      refundDelivered: {
        title: 'Retoure',
        short: 'Ret',
        description: 'In der Periode retournierte Produkte',
      },
      clearingStock: {
        title: 'Abrechnungsmenge',
        short: 'Abr.',
        description: 'Differenz der in der Periode verkauften und retournierten Einheiten',
      },
      closingStock: {
        title: 'Endbestand',
        short: 'Ende',
        description: 'Bestand am Ende der Periode',
      },
      sold: {
        title: 'Verkauft Unabhängig Vom Lieferstatus',
        short: 'Verk.',
        description: 'Alle in der Periode verkauften Produkte, ohne Berücksichtigung des Lieferstatus.',
      },
      refund: {
        title: 'Erstattet Unabhängig Vom Lieferstatus',
        short: 'Erst.',
        description: 'Alle in der Periode erstatteten Produkte, unabhängig vom Lieferstatus.',
      },
      soldUnfulfilled: {
        title: 'Offene Bestellungen',
        short: 'Offen',
        description: 'Alle verkauften, aber noch nicht ausgelieferten Produkte',
      },
      reserved: {
        title: 'Reserviert',
        short: 'Res.',
        description: 'Produkte aus Bestellungen, die auf Zahlungseingang warten oder ähnliches.',
      },
      availableStock: {
        title: 'Verfügbarer Bestand',
        short: 'Verf.',
        description: 'Produkte die am Ende der Periode verfügbar waren.',
      },
      netSold: {
        title: 'Net Verkauft',
        short: 'Net Verk.',
        description: 'Verkauft, minus Erstattet',
      },
    },
    shipping: 'Shipping',
    shippingRequirements: {
      _requirements: {
        over18: {
          title: '18+',
          description:
            'Das Alter der Empfänger:in wird bei der Zustellung überprüft. Auslieferung erfolgt nur an  Personen mit 18+ Jahren',
        },
      },
      none: 'Keine speziellen Versandanforderungen',
    },
    shippingTags: {
      _tags: {
        warenpost: {
          prohibitedTitle: 'Keine Warenpost',
          prohibitedDescription: 'Dieses Produkt ist zu groß oder zu schwer, um als Warenpost versendet zu werden.',
        },
      },
    },
    musicCharts: {
      _name: 'Musik Charts',
      recordLabel: 'Label',
      recordDistributor: 'Distributor',
      recordReleasedAt: 'Veröffentlichungsdatum',
      recordType: 'Medium',
      editMusicCharts: 'Musik Charts Angaben bearbeiten',
      uploadOverrides: 'Music Charts Overrides hochladen',
      downloadChartOverrides: 'Derzeitige Music Charts Overrides herunterladen',
    },
    bookCharts: {
      _name: 'Bücher Charts',
      gtin: 'GTIN / ISBN',
      productGroup: 'Warengruppe',
      description1: 'Artikelbezeichnung 1',
      description2: 'Artikelbezeichnung 2',
      releasedAt: 'Veröffentlichungsdatum',
      edit: {
        title: 'Buch Charts Angaben bearbeiten',
      },

      descriptions: {
        productGroup: 'Bezeichnung der Warengruppe wie z.B."Buch", "Games", usw.',
        gtin: 'EAN/ISBN numerisch ohne Leerzeichen oder Bindestriche',
        description1: 'Titel des Buches, Spiels, etc.',
        description2: 'Autor des Buches oder Interpret, Art oder System für elektronische Spiele',
        releasedAt: 'In den meisten Fällen deckungsgleich mit dem Veröffentlichungsdatum des Produktes',
      },
    },
    productVariant: {
      _name: 'Produktvariante',
      _plural: 'Produktvarianten',
      activeFrom: 'Aktiv ab',
      activeUntil: 'Aktiv bis',
      ean: 'EAN',
      eanInvalid: 'kein gültiger EAN oder UPC Code',
      eanValid: 'gültiger EAN Code',
      upcValid: 'gültiger UPC Code',
      pricingError: 'Du musst zunächst mindestens eine Produktvariante anlege, bevor du Preise festlegen kannst.',
      sku: 'SKU',
      private: 'Nicht im Shop anzeigen',
      resaleAllowed: 're:sale erlauben',
    },
    warehouseLocation: {
      _name: 'Lagerplatz',
      _plural: 'Lagerplätze',
    },
    reference: {
      _name: 'Referenz',
      _plural: 'Referenzen',
      merchCreditorReference: 'Kreditorenreferenz (Merch)',
      eventCreditorReference: 'Kreditorenreferenz (Event)',
    },
    refund: {
      _name: 'Rückerstattung',
      _plural: 'Rückerstattungen',
      refundItems: 'Produkte zurückerstatten',
      refundAndRelease: 'zurückerstatten und freigeben',
      refundAndDestroy: 'zurückerstatten und Kontingent verringern',
      refundAndBlock: 'zurückerstatten und sperren',
      reIssueTickets: 'Tickets neu ausstellen',
      refundModal: {
        refundOptionsTickets: (tickets: number) => `Stornooptionen ${pluralize(tickets, 'Ticket')}`,
        refundOptionsMerch: 'Stornooption Merch',
        mixedWarning: (tickets: number) => `Du erstattest eine Auswahl aus Merch und ${pluralize(tickets, 'Ticket')}.`,
        markDeliveryAsSent: 'Eingehenden Lieferschein sofort buchen',
      },
    },
    role: {
      editor: 'Editor',
      observer: 'Betrachter',
      owner: 'Eigentümer',
      EDITOR: 'Editor',
      OBSERVER: 'Betrachter',
      OWNER: 'Eigentümer',
    },
    sale: {
      _name: 'Verkauf',
      _plural: 'Verkäufe',
      requestSale: 'Verkauf beantragen',
      saleLaunch: 'Verkauf beginnen',
      salesStats: 'Verkaufsstatistiken',
      startSale: 'Verkauf starten',
      failedToLoad: 'Laden der Verkaufsdaten fehlgeschlagen',
      empty: 'leer',
      returned: (tickets: number) => `${tickets} Tickets wurden erstattet`,
      salesStatistics: 'Verkaufsstatistik',
      salesByPricingCategory: 'Ausgestellte Tickets nach Preiskategorie',
      summary: 'Summe',
      lessThanZeroEuro: 'Freikarten',
      moreThanZeroEuro: 'Regulärer Verkauf',
      noSalesInPeriod: 'Keine Verkäufe in diesem Zeitraum',
      totalSales: 'Gesamtumsatz',
    },
    shop: {
      _name: 'Shop',
      _plural: 'Shops',
      allShops: 'Alle Shops',
      shopIndependent: 'Shop unabhängig',
      defaultLocale: 'Standardsprache',
      domain: 'Domain',
      logo: 'Logo',
      meta: 'Meta',
      parent: 'Eltern',
      paymentBlacklist: 'Deaktivierte Zahlungsmethoden',
      siteMap: 'Google Sitemap',
      waitingQueue: 'Warteschlange',
      subtitleInShop: 'Untertitel im Shop anzeigen',
      orderMergingAllowed: 'Nachbestellungen gestatten',
      shopsCategories: 'Shops / Kategorien',
      shopsAndCategories: 'Shops & Kategorien',
      shopsAndCategoriesModal: 'Shop & Kategorien Zuordnung',
      slug: 'Slug',
      slugFormat:
        'Darf nur Buchstaben und Zahlen enthalten und ggf. mit einem Bindestrich getrennt werden. Beispiel: cooler-shop',
      freeShippingThreshold: 'Mindestbetrag für kostenlosen Versand',
      customEmailSender: 'E-Mails anpassen',
      customEmailSenderFields: {
        email: 'E-Mail Adresse',
        displayName: 'Anzeigename',
        headerImage: 'Header Grafik',
        bodyBackgroundColor: 'Hauptteil Hintergrundfarbe',
        bodyForegroundColor: 'Hauptteil Vordergrundfarbe (z.B. Text)',
        buttonBackgroundColor: 'Button Hintergrundfarbe',
        buttonForegroundColor: 'Button Vordergrundfarbe (z.B. Text)',
        footerBackgroundColor: 'Footer Hintergrundfarbe',
        footerForegroundColor: 'Footer Vordergrundfarbe (z.B. Text)',
        closingNotes: 'Schlusstext',
        emailBackgroundColor: 'Email Hintergrundfarbe',
        separatorLineColor: 'Trennlinienfarbe',
      },
      customSenderAddress: 'Benutzerdefinierte Absenderadresse',
      useCustomSenderAddress: 'Benutzerdefinierte Absenderadresse verwenden?',
      customSenderAddressFields: {
        name1: 'Name 1',
        name2: 'Name 2',
        name3: 'Name 3',
        address: 'Straße und Hausnummer',
        address2: 'Adresszustz 1',
        address3: 'Adresszustz 2',
        postCode: 'PLZ',
        city: 'Ort',
        countryCode: 'Länderkennung',
        contact: 'Ansprechpartner',
        email: 'E-Mail-Adresse',
        phone: 'Telefonnummer',
      },
    },
    status: {
      _name: 'Status',
      active: 'Aktiv',
      activeFrom: 'Aktiv ab',
      activeNow: 'Jetzt Aktiv',
      archived: 'Archiviert',
      available: 'Verfügbar',
      blocked: 'Gesperrt',
      booked: 'Gebucht',
      bookedCount: 'Gebucht',
      canceled: 'Storniert',
      cart: 'Warenkorb',
      done: 'Fertig',
      draft: 'Entwurf',
      failed: 'Fehlgeschlagen',
      processing: 'Processing',
      expressProcessing: 'Express Checkout',
      fulfillment: 'Wird gepackt',
      incomplete: 'Unvollständig',
      inReview: 'Wird überprüft',
      inactive: 'Inaktiv',
      onSale: 'Im Verkauf',
      paid: 'Bezahlt',
      paymentPending: 'Zahlung ausstehend',
      pending: 'ausstehend',
      pendingFulfillment: 'Lieferung ausstehend',
      pendingPayment: 'Zahlung ausstehend',
      permanent: 'dauerhaft gesperrt',
      private: 'nicht öffentlich',
      problem: 'Problem',
      published: 'Veröffentlicht',
      reserved: 'Reserviert',
      refundPending: 'Erstattung ausstehend',
      scheduled: 'Geplant',
      sent: 'gesendet',
      sold: 'Verkauft',
      stopped: 'Verkauf gestoppt',
      unpublished: 'Unpublished',
      waiting: 'Waiting',
      local: 'Lokal',
      systemFree: '+ Frei / Begleittickets',
      systemSold: 'Verkauft (System)',
    },
    ticket: {
      _name: 'Ticket',
      _plural: 'Tickets',
      amount: (count: number) => (count === 1 ? `${count} Ticket` : `${count} Tickets`),
      additionalNotes: 'Additional Notes',
      hardticket: 'Hardticket',
      head: 'Head',
      logoTags: 'Tags',
      onlineticket: 'Online',
      presenterLogo: 'Präsentatoren Logos',
      ticketPreview: 'Vorschau Ticket',
      ticketPreviewFullPdf: 'Komplette Vorschau als PDF',
      someExplanation: 'Some explanation text',
      ticketImage: 'Ticket Image',
      ticketPersonalization: 'Ticket Personalisierung',
      ticketTypes: 'Ticket Typen',
      ticketing: 'Ticketing',
      ticketline: 'Ticket Zeile',
      updateTicketInfo: 'Ticket Details ändern',
      updateGeneralInformation: 'Allgemeine Information ändern',
      numberOfTickets: 'Anzahl Tickets',
      soldTicketsDist: 'Verkäufe nach Auslieferungsvariante',
      noTicketsSold: 'Keine Verkäufe',
      personalIdentifierDisclaimer: 'Ticket-Informationstext zur Personalisierung',
      forcePrintPersonalization: 'Personalisierung auf Ticket erzwingen',
      forcePrintPersonalizationDescription:
        'Eine vorhandene Personalisierung wird auf das Ticket gedruckt, auch wenn die Personalisierung für die Veranstaltung deaktiviert ist.',
      updatePersonalIdentifier: 'Personalisierung überschreiben',
      personalIdentifier: 'Personalisierung',
      personalIdentifierDescription: 'Name des Besuchers, wie er im Ausweis steht',
      ticketLayout: 'Ticket Layout',
      onlineTicketLayout: 'Onlineticket Layout',
      localTicketLayout: 'Lokales Ticket Layout',
      headerImage: 'Titelgrafik',
      pricingVariant: 'Preisvariante',
      ticketNumber: 'Ticket Nummer',
      orderHistory: 'Ticket Historie',
      cancelTickets: 'Tickets stornieren',
      refundOptions: 'Stornooptionen',
      cancelAndRelease: 'Stornieren und freigeben',
      cancelAndBlock: 'Stornieren und sperren/optionieren',
      printedTickets: 'Tickets drucken',
      localPricing: 'Lokale Preise',
      systemPricing: 'System Preise',
      design: 'Design',
      selectFreeSeats: 'Selektiere freie Sitze oder einen Block, um Tickets zu erzeugen.',
      personalized: 'personalisiert',
      notPersonalized: 'nicht personalisiert',
      checkedIn: 'Eingecheckt',
      notCheckedIn: 'Nicht eingecheckt',
      checkInOpens: 'Check-In öffnet',
      checkInCloses: 'Re-Check-In schließt',
      checkInClosesExplanation:
        'Ab diesem Zeitpunkt können Check-In Daten nicht durch neue Daten überschrieben werden. Der erste Check-In ist weiterhin möglich.',
      selectedTickets: 'Selektierte Tickets',
      issueTickets: 'Tickets ausstellen',
      overview: 'Übersicht',
      seat: 'Sitzplatz',
      codes: 'Codes',
      used: 'benutzt',
      unused: 'unbenutzt',
      ticketScanner: 'Ticket Scanner',
      enableTicketScanner: 'Ticket Scanner aktivieren',
      eventCodes: 'Event Codes',
      resale: 're:sale',
      q: 'Q',
      search: 'Suche nach Empfänger*in, E-Mail Adresse, Tags',
      recipient: 'Empfänger*in',
      pricingCategory: 'PK',
      emailDeliveryDates: 'GEsendet am',
      notAvailable: 'N/A',
      commentEmailText: 'Kommentar / E-Mail-Text',
      noTicketsIssued: 'Keine Tickets ausgestellt',
      pricingCategories: 'Preiskategorie',
      productVariant: 'Produktvariante',
      venueSection: 'Bereich / Block',
      preview: 'Vorschau',
      hardProductVariant: (description: string) => `${description} Hard`,
      onlineProductVariant: (description: string) => `${description} Online`,
      passbook: 'Wallet-Ticket',
      ticketPassword: 'Ticketpasswort',
      resaleOffer: {
        _name: 're:sale Angebot',
        onResale: 'im re:sale',
        repurchasedInOrder: 'wiederverkauft in Bestellung',
        public: 'öffentlich',
        private: 'privat',
      },
      resoldByOffer: {
        originalOrder: 'Ursprüngliche Bestellung',
      },
      rerender: {
        title: 'Tickets neu generieren',
        description: 'Es dauert einige Minuten, bis die Tickets neu generiert werden.',
        online: 'Onlinetickets neu generieren',
        hard: 'Hardtickets neu generieren',
      },
    },
    resendTickets: {
      confirmResendingEmail: 'Bestätige das erneute Senden der Tickets',
      title: 'Bist du dir sicher, dass Sie die Tickets erneut an den Empfänger senden möchten?',
      subtitle:
        'Durch diese Aktion wird eine neue E-Mail mit den ausgestellten Tickets generiert und an die folgenden Details gesendet:',
      name: 'Name',
      recipientEmailAddress: 'E-Mail-Adresse des Empfängers:',
      noOfTickets: 'Anzahl der Tickets:',
      resend: 'Erneut Senden',
      success: 'Tickets erfolgreich erneut gesendet',
    },
    scanner: {
      _name: 'Scanner',
      _plural: 'Scanner',
      addNew: 'Scanner hinzufügen',
      delete: 'Scanner löschen',
      loadingQR: 'QR Code wird geladen...',
      errorQR: 'Es konnte für diesen Scanner kein QR Code generiert werden',
    },
    tour: {
      _name: 'Tour',
      _plural: 'Touren',
      comment: 'Kommentar',
      displayTitle: 'Anzeige Titel',
      displayTitleMessage: 'e.g. Bands, Support-Bands zur Verbesserung der Suche',
      excludeFromFeeds: 'Exclude from Feeds (Facebook & Google)',
      internalTitle: 'Interner Titel',
      showSubtitle: 'Untertitel im Shop anzeigen',
      sideImage: 'Side Image',
      sortOrder: 'Sort order',
      sortOrderHomepage: 'Sort order homepage',
      sortOrderTicketBottom: 'Sort order tickets / bottom',
      sortOrderTicketTopLeft: 'Sort order tickets / top left',
      sortOrderTicketTopRight: 'Sort order tickets / top right',
      tableInternalTitle: 'Internal Title (Title)',
      noTours: 'Keine Suchergebnisse in Touren',
      eventsInThisTour: 'Veranstaltungen',
      noEvents: 'Keine Veranstaltungen in dieser Tour',
      meta: 'Meta',
    },
    tourArtists: {
      _name: 'Verbundene Artists',
      _plural: 'Artists',
    },
    comment: {
      _name: 'Kommentar',
      _plural: 'Kommentare',
    },
    user: {
      _name: 'Benutzer',
      _plural: 'Benutzer',
      admin: 'Admin',
      contactDetails: 'Kontaktdaten',
      customer: 'Kunde',
      email: 'Email',
      firstName: 'Vorname',
      lastName: 'Nachname',
      name: 'Name',
      phone: 'Telefon',
    },
    variant: {
      _name: 'Variante',
      _plural: 'Varianten',
    },
    venue: {
      _name: 'Venue',
      _plural: 'Venues',
      allVenues: 'Alle Venues',
      eventsForVenue: 'Events in diesem Venue',
      location: 'Location',
      venueName: 'Venue',
      noEvents: 'Keine Veranstaltungen in diesem Venue',
    },
    venueLayout: {
      _name: 'Saalplan',
      _plural: 'Saalpläne',
      contingent: 'Kontingente',
      importStatus: {
        pending: 'wird importiert',
        done: null,
        error: 'Import fehlgeschlagen',
      },
      noContingent: 'Kein Kontingent in diesem Bereich',
      quickNav: 'Schnellzugriff',
      seatmaps: 'Saalplan',
      seats: 'Sitzplätze',
      seatsCsv: 'Sitzplätze CSV',
      seatsAndAllocation: 'Kontingente, Bereiche und Sitzplätze',
      seatsAndBlocks: 'Sitze und Blöcke',
      section: 'Bereich',
      selected: 'Selektiert',
      name: 'Name',
      capacity: 'Kapazität',
      capacitySetBySeatMap: 'Capacity is set by Seat map.',
      archived: 'Archiviert',
      multiplePricingCategories: 'Mehrere Preiskategorien',
      selectedSeatsAndBlocks: 'Ausgewählte Sitze und Blöcke',
      numberOfSelectedSeats: 'Insgesamt ausgewählte Sitzplätze',
      currentPricingCategory: 'Aktuelle Preiskategorie:',
      selectCategory: 'Neue Preiskategorie auswählen',
      addRowToSelection: 'Reihe der Auswahl hinzufügen',
      addSectionToSelection: 'Sektion der Auswahl hinzufügen',
      pleaseSelectPricingCategory: 'Bitte wähle eine Preiskategorie aus',
      createEventWithThisLayout: 'Event mit diesem Layout erstellen',
    },
    server: {
      configuration: 'Server Konfiguration',
      status: 'Server Status',
      scalingSchedule: 'Geplante Skalierung',
      serverMode: 'Server Modus',
      activeOn: 'Aktiv ab',
      actions: 'Aktionen',
      createSchedule: 'Skalierung planen',
      deleteSchedule: 'Geplante Skalierung löschen',
      noSchedules: 'Keine geplanten Skalierungen',
      confirmDeleteSchedule: 'Bist du dir sicher, dass du die geplante Skalierung löschen möchtest?',
    },
    news: {
      _name: 'Post',
      _plural: 'News',
      untitled: 'Post ohne Titel',
      regular: 'Normal',
      hotNews: 'Hot News',
      featureNews: 'Featured News',
      inverse: 'Invertiert',
      inverseDescription: 'Invertierte Nachrichten zeigen weißen Text auf dunklen Hintergründen im Shop an.',
      deleteNewsItem: {
        title: 'News löschen',
        description: 'Soll der Beitrag wirklich gelöscht werden?',
        confirm: 'Löschen',
      },
      order: 'Reihenfolge',
      image_video: 'Bild/Video',
      meta: 'Meta',
    },
    agreement: {
      _name: 'Agreement',
      _plural: 'Agreements',
      title: 'Bezeichnung',
      partner: 'Vertragspartner',
      postTax: 'Berechnungsgrundlage',
      vatOverride: 'Vom Verkauf abweichender Steuersatz',
      type: 'Abrechnungstyp',
      description: 'Beschreibung auf Rechnung',
    },
    contingent: {
      capacityAndAvailability: 'Kapazität und Verfügbarkeit',
      capacity: 'Kapazität',
      issued: 'Ausgestellt',
      reserved: 'Reserviert',
      shoppingCart: 'Warenkorb',
      availableTickets: 'Verfügbare Tickets',
      blocks: 'Sperrungen',
      options: 'Optionen',
      noBlocksOrOptionsYet: 'Noch keine Sperrungen oder Optionen angelegt',
      adjustCapacity: 'Kapazität anpassen',
      freeTickets: 'Tickets',
      generateFreeTickets: 'Tickets ausstellen',
      hereYouCanGenerateFreeTickets:
        'Hier kannst du Tickets für Gäste und andere Anlässe wie z.B. Abendkasse erzeugen.',
      newTickets: 'Neue Tickets',
      newTicketsWillBeCreated:
        'Neue Tickets werden erzeugt wenn die Kapazität erhöht wird. Was soll mit den Tickets passieren?',
      makeAvailable: 'Verfügbar machen',
      blockThem: 'Sperren / Optionieren',
      pool: 'Option',
      editContingent: 'Kontingente bearbeiten',
      jumpToArea: 'Zum Bereich springen',
      createPools: 'Sperrungen / Optionen anlegen',
      createPoolsExplanation:
        'Hier kannst du Sperrungen & Optionen für z.B. Gäste oder bisher ungenutzte Kapazitäten anlegen.',
      createPoolButton: 'Sperren / Optionieren',
      blockReason: 'Sperrungsgrund',
      noneCreated: 'Keine angelegt',
      createBlocks: 'Sperrungen / Optionen anlegen',
    },
    automation: {
      startNow: 'Jetzt starten',
      automation: 'Automatisierung',
      stopNow: 'Jetzt stoppen',
      startCampaignAt: 'Kampagne starten am',
      stopCampaignAt: 'Kampagne stoppen am',
      setAutomation: 'Automatisierung setzen',
      editAutomation: 'Automatisierung bearbeiten',
      operation: {
        start: 'Start',
        stop: 'Stop',
      },
    },
  },
  page: {
    sidebar: {
      fulfillment: 'Versand',
      settings: 'Einstellungen',
      backPayments: 'Rückzahlungen',
      build: 'build',
      lastUpdatedAt: 'Zuletzt aktualisiert am',
      contentAndCampaigns: 'Inhalte & Kampagnen',
      voucherCampaigns: 'Gutschein Kampagnen',
    },
    fulfillmentDetails: {
      productsToPack: 'Packliste',
      scanProductOrSku: 'Scanne ein Barcode, tippe die SKU oder Ticketcode',
      validationCode: 'Ticketcode',
      productName: 'Produkt',
      bookDeliveryNote: 'Lieferschein buchen',
      deliverySuccess: 'Lieferschein erfolgreich gebucht',
      orderSent: 'Bestellung gesendet',
      packedProducts: 'Gepackte Produkte',
      printShippingLabel: 'Versandschein drucken',
      printTicket: 'Ticket drucken',
      printTickets: 'Tickets drucken',
      printDHLLabel: 'DHL Label drucken',
      scanLabel: 'DHL Label scannen',
      noProductsToFulfill: 'Keine Produkte zu packen',
      problematicOrderTitle: 'Problem mit der Bestellung',
      problematicOrderText:
        'Die Bestellung hat den Status „Problem“ und sollte wahrscheinlich noch nicht verschickt werden.',
      fulfillAnyway: 'Versand trotzdem starten',
      confirmBooking: 'Buchung bestätigen',
      confirmBookingDescription: (code: string) => `Bist du sicher, dass du mit dem Code ${code} fortfahren möchtest?`,
    },
    dashboard: {
      editTeam: 'Edit Team',
      preview:
        'This software is an early preview. The shown data comes without any guarantee of correctness. All current functionality is subject to future changes.\nCurrently you can: \n- update your personal account (e.g. your contact data and your avatar) \n- edit your team (e.g. upload a logo or invite other team members) \n- have a look at your sales report\nIf you find an error or have other helpful insights please send an email with the subject “Admin Bug Report” to info@krasserstoff.com or just give us a call at 030-632222-422.\n',
      title: 'Dashboard',
      welcome: "Welcome - You're working with alpha software, therefore we have some notes",
      noDataToShow: 'Es gibt nichts, was hier angezeigt werden kann',
      resetTimeframe: 'Zeitraum zurücksetzen',
    },
    login: {
      checkMail: 'Bitte überprüfe deine E-Mails.',
      confirmPassword: 'Passwort wiederholen',
      forgotPassword: 'Passwort vergessen',
      loginFailed: 'Login fehlgeschlagen, bitte versuch dich erneut anzumelden',
      loginFormTitle: 'Du musst dich anmelden um fortzufahren',
      newPassword: 'Neues Passwort eingeben',
      password: 'Passwort',
      rememberLogin: 'Eingeloggt bleiben',
      resetPassword: 'Passwort zurücksetzen',
      title: 'Login',
      createAccount: 'Konto erstellen',
      hasAccount: 'Wenn du bereits ein Konto bei Krasser Stoff hast, kannst du dich damit einloggen',
    },
    turnover: {
      menuTitle: 'Umsätze',
      title: 'Umsätze',
      summary: 'gesamt',
      loadMore: 'mehr laden',
      date: 'Datum',
      product: 'Name',
      sku: 'Event',
      pkVariant: 'PK / Variante',
      count: 'Anzahl',
      brutPrice: 'Preis (brutto)',
      brutTotal: 'Umsatz (brutto)',
      vatRate: 'USt Satz',
      vat: 'USt Betrag',
      downloadAsPdf: 'Als PDF herunterladen',
    },
    signup: {
      title: 'Signup',
      signupFormTitle: 'Zum Fortfahren anmelden',
      userHasInvitedYou: ({ invitedBy, company, role }: Partial<Membership>) => {
        const user = userNameAndOrEmail(invitedBy?.name, invitedBy?.email);
        const translatedRole = role ? I18n.translate.model.role[role] : '';
        return `${user} hat dich eingeladen, <strong>${company?.name}</strong> als ${translatedRole} beizutreten.`;
      },
      pleaseSignUp: 'Bitte melde dich an, um die Einladung anzunehmen',
      password: 'Passwort festlegen',
      confirmPassword: 'Bestätige dein Passwort',
      tos: 'Ich bestätige, dass ich die Allgemeinen Geschäftsbedingungen und die Datenschutzrichtlinie (die von Zeit zu Zeit aktualisiert werden können) gelesen und verstanden habe, und erkläre mich hiermit einverstanden, an diese Bedingungen gebunden zu sein.',
      alreadyHaveAccount: 'Du hast bereits ein Konto?',
    },
    queue: {
      active: 'im Shop',
      activeFor: (names: string) => `Aktiv für ${names}.`,
      free: 'freie Kapazität',
      title: 'Warteschlange',
      waiting: 'warten',
      waitingQueueStats: 'Warteschlangenstatistik',
      sessionInactivity: 'Session Inaktivität',
      activeSessions: 'Aktive Sessions',
      sessions: 'Sessions',
      waitingTime: 'Wartezeit',
      maximumSpotsCount: 'Plätze im Checkout',
      maximumCatalogSpotsCount: 'Plätze im Shop',
      ordersCreatedPerMinute: 'Erstellte Bestellungen pro Minute',
      ordersPerMinute: 'Bestellungen pro Minute',
      paymentsPerMinute: 'Bezahlungen pro Minute',
      checkoutQueue: 'Checkout Queue',
      catalogQueue: 'Catalog Queue',
      topIps: 'Top 10 IP-Adressen nach erstellten Bestellungen in den letzten 120 Minuten',
      topPaymentFingerprints: 'Top 10 Zahlungs-Fingerabdrücke nach erstellten Bestellungen in den letzten 30 Minuten',
    },
    settings: {
      title: 'Einstellungen',
      ordersAutoCancel: 'Autostorno',
    },
    styleguide: {
      title: 'Styleguide',
    },
    salesReport: {
      downloadButton: 'VVK Report',
      title: 'VVK Report herunterladen',
      explanation: 'Wähle die Informationen aus, die im Vorverkaufsbericht enthalten sein sollen.',
      priceParts: 'Preisbestandteile',
      prices: 'Preise',
      generatePdf: 'PDF erstellen',
      inclFreeLocalTickets: 'inkl. Lokale Tickets = 0,00 EUR',
      inclPaidLocalTickets: 'inkl. Lokale Tickets > 0,00 EUR',
      basePrice: 'Basispreis',
      fee: 'VVK Gebühr & System Gebühr',
    },
    ticketScanner: {
      valid: 'Gültiges Ticket',
      invalid: 'Unbekanntes Ticket',
      checked: 'Bereits eingelassen',
      checkIn: 'Einlasszeit',
      gate: 'Eingang',
      scanTicket: 'Scannen',
      enterCode: 'Code eingeben',
      startScanning: 'Scan',
      stopScanning: 'Scanner beenden',
      checkout: 'Einlass aufheben',
      enterTicketNumber: 'Ticketnummer eingeben',
      submit: 'OK',
      activityLog: 'Aktivitäten',
      noActivity: 'Noch keine Aktivität',
      scan: 'Scan',
      fewSecondsAgo: 'vor ein paar Sekunden',
      section: 'Abschnitt',
      row: 'Reihe',
      seat: 'Sitz',
      manualEntry: 'Manuelle Eingabe',
      checkedOut: 'Ticket wieder gültig gemacht',
      scanCodeToGetStarted: 'Bitte scanne den Event-Code, um zu starten.',
      invalidEventCode: 'Dies scheint kein gültiger Event-Code zu sein.',
      contactUs: 'Du brauchst Hilfe? Kontaktiere uns!',
      loadingText: 'Die Ticketcodes werden gerade abgerufen. Bitte warten Sie.',
      checkOutWarning: 'Einmal ausgecheckt, ist das Ticket wieder gültig und kann erneut verwendet werden',
      checkOutWarningDismiss: 'Nicht mehr anzeigen',
      checkOutWarningAccept: 'Ich verstehe!',
    },
    event: {
      salesByGeography: 'Verkäufe nach Geografie',
      salesByGeographyTooltip:
        'Die Geografie wird anhand der Postleitzahl der Bestellenden ermittelt. In Ländern, für die keine detaillierten Postleitzahleninformationen hinterlegt sind, wird die Summe aller Bestellungen des Landes in einem schwarzen Quadrat zusammengefasst. Die Verteilung bezieht sich auf die bestellenden Personen, nicht auf die Ticketnutzer*Innen. Sie erlaubt daher nur bedingt Rückschlüsse auf Besucher*Innen.',
      salesByGender: 'Verkäufe nach Geschlecht',
      salesByGenderTooltip:
        'Das Geschlecht wird anhand des Vornamens der Bestellenden ermittelt. Diese Methode erspart Kund*Innen die Frage nach der Zuordnung zu einem Geschlecht und erlaubt eine relativ gute und missbrauchssichere Bestimmung der gelebten Zuordnung. Achtung: Die Verteilung bezieht sich auf die bestellenden Personen, nicht auf die Ticketnutzer*Innen. Sie erlaubt daher nur bedingt Rückschlüsse auf Besucher*Innen.',
      ticket: {
        print_section_ticket_overlay: {
          tags_description: 'z.B. Künstlerteam/Familie/Freunde',
          send_via_email: 'Per E-Mail senden',
          email_text: 'E-Mail Text',
        },
      },
      details: {
        ticketDelay: {
          cardTitle: 'Verzögerte Onlinetickets',
          noAutomation: 'Keine Automatisierung konfiguriert',
          ticketsWillBeSentAt: 'Tickets werden verschickt ab',
          sendTicketsAt: 'Tickets verschicken ab',
          removeAutomationHint:
            'Tipp: Setze den Zeitpunkt in die Vergangenheit, um alle Tickets so schnell wie möglich zu verschicken. Die Automatisierung wird danach automatisch entfernt.',
        },
      },
      design: {
        passbook: {
          title: 'Wallet-Ticket',
          enabled: 'Wallet-Tickets für Onlinetickets ausstellen',
          enabledTrue: 'Wallet-Tickets werden für Onlinetickets ausgestellt.',
          enabledFalse:
            'Wallet-Tickets werden nicht ausgestellt. Existierende Wallet-Tickets können weiterhin heruntergeladen werden.',
          preview: 'Vorschau herunterladen',
          edit: {
            enabled: 'Wallet-Tickets für Onlinetickets ausstellen',
            title: 'Wallet-Ticket-Einstellungen',
          },
        },
        publicTransport: {
          title: 'Öffentliche Verkehrsmittel',
          description: 'Beschreibung',
          company: 'Unternehmen',
          edit: {
            enabled: 'Informationen zu öffentlichen Verkehrsmitteln einfügen',
            title: 'Öffentliche Verkehrsmittel bearbeiten',
            description: 'Beschreibung',
            company: 'Unternehmen',
          },
        },
        ticketLayouts: {
          title: 'Ticket Layout',
          edit: {
            title: {
              local: 'Local Ticket Layout bearbeiten',
              hard: 'Ticket Layout bearbeiten',
              online: 'Online Ticket Layout bearbeiten',
            },
            customHtml: 'Benutzerdefiniertes HTML',
            previewError: 'Fehler beim Rendern der Vorlage. Bitte überprüfen Sie Ihr HTML- und Liquid-Syntax.',
          },
        },
      },
      utmTrackingSourcesCard: {
        noUtmData: 'Keine UTM-Daten',
      },
      utmTrackingCampaignsCard: {
        noUtmData: 'Keine UTM-Daten',
      },
      utmTrackingMediaCard: {
        noUtmData: 'Keine UTM-Daten',
      },
      create: {
        venueLayout: 'Saalplan',
        pricingCategory: {
          _name: 'Preiskategorie',
          _plural: 'Preiskategorien',
        },
        capacity: 'Kapazität',
        addEvent: 'Hinzufügen Veranstaltung',
        venue: 'Venue',
        venues: 'Venues',
        blockAllSeats: 'Alle Sitzplätze am Anfang sperren?',
        yes: 'Ja',
        no: 'Nein',
        event: 'Veranstaltung',
        ticketline1: 'Ticket Zeile 1',
        ticketline2: 'Ticket Zeile 2',
        ticketline3: 'Ticket Zeile 3',
        ticketlineHeadline: 'Ticket Zeile Kopfzeile',
        ticketlineFooter: 'Ticket Zeile Fußzeile',
        tourOperator: 'Tourveranstalter',
        localOperator: 'Lokaler Veranstalter',
        description: 'Beschreibung',
        excludeFromFeeds: 'Von Feeds ausschließen (Facebook & Google)',
        datesAndTimes: 'Daten und Zeiten',
        startDate: 'Start Date',
        multiday: 'mehrtägig',
        endDate: 'Enddatum',
        activeFrom: 'Aktiv ab',
        doorTime: 'Einlass',
        stageTime: 'Beginn',
        personalization: 'Personalization',
        formatHHMM: 'Format: HH:MM',
        noPersonalizationRequired: 'Keine Personalisierung',
        groupPersonalizationRequired: 'Gruppen-Personalisierung (alle Tickets gleicher Name)',
        ticketPersonalizationRequired: 'Einzel-Personalisierung (ein Name per Ticket)',
        contactTracingRequired: 'Contact-Tracing',
        checkInRequired: 'Check-In',
        legacyId: 'ID (altes System)',
        shopSettings: 'Shop Einstellungen',
        publishDate: 'Sichtbar ab',
      },
    },
    voucherCampaigns: {
      voucherCampaigns: 'Gutschein Kampagnen',
      voucherType: 'Gutschein Typ',
      product: 'Produkt Rabatt',
      shipping: 'Versand Rabatt',
      codes: 'Codes',
      usageCodes: 'Nutzung / Codes',
      title: 'Titel',
      createdOn: 'Erstellt am',
      status: 'Status',
      automations: 'Automations',
      start: 'Start',
      stop: 'Stop',
      campaignDetails: 'Kampagnen Details',
      form: {
        create: 'Gutschein Kampagne erstellen',
        edit: 'Gutschein Kampagne bearbeiten',
        shop: 'Shop',
        title: 'Titel',
        internalTitle: 'interne Titel',
        target: 'Gutschein Typ und Rate',
        targetDescription: 'Wähle den Gutschein Typ und wie die Kosten aufgeteilt werden.',
        targetProduct: 'Auf Merchandise / Events',
        targetShipping: 'Auf Versand',
        amount: 'Betrag',
        type: 'Typ/Einheit',
        discountTypePercentage: 'Prozent',
        discountTypeFixed: 'Fest',
        amountDescription:
          'Rabatte können absolut oder prozentual sein Betrag und Rabattart eingeben Z.B.: 20% Rabatt auf Merchandis',
        costSplit: 'Kostenverteilung',
        costSplitDescription: (value: string) =>
          `Der Künstler zahlt <strong>${value}</strong> der Kosten für den verwendeten Gutschein.`,
        voucherUsage: 'Gutschein Nutzung',
        voucherUsageDescription:
          'Wählen Sie aus, ob die Gutscheine dieser Kampagne einmal oder mehrmals eingelöst werden können.',
        multiple: 'Mehrfach',
        single: 'Einmalig',
        amountPercentageError: 'Bitte geben Sie einen gültigen Betrag zwischen 0 und 100 ein',
      },
    },
    voucherCampaign: {
      voucherCampaigns: 'Gutschein Kampagnen',
      vouchers: 'Gutscheine',
      campaignDetails: 'Kampagnen Details',
      shop: 'Shop',
      voucherTypeAndRate: {
        title: 'Gutschein Typ und Rate',
        use: {
          multiple: 'Mehrfache Verwendung',
          single: 'Einmalige Verwendung',
        },
        type: {
          absolute: 'Absolut-basiert',
          percentage: 'Prozentsatzbezogen',
        },
      },
      codeUsage: 'Code Nutzung',
      contractDiscountShare: 'Vertragsrabattanteil',
      delete: {
        title: 'Gutschein Kampagne löschen',
        description: 'Möchtest du diese Gutschein Kampagne wirklich löschen?',
        confirm: 'Löschen',
      },
      voucherCodes: {
        voucherCodes: 'Gutscheincodes',
        code: 'Code',
        totalUsed: 'Insgesamt verwendet',
        noVoucherCodes: 'Keine Gutscheincodes',
        singleResultSelected: '1 Suchergebnis ausgewählt',
        manyResultsSelected: (count: number) => `${count} Suchergebnisse ausgewählt`,
        allSelected: 'Alle Suchergebnisse ausgewählt',
        selectAll: 'Alle auswählen',
        downloadCodes: 'Codes herunterladen',
        create: {
          voucherCode: 'Gutscheincode',
          enterCode: 'Code eingeben',
          generateCodes: 'Codes generieren',
          codeDescription: "Z.B. 'MOTHERSDAY24'",
          amountOfCodes: 'Anzahl der Codes',
        },
        import: {
          importCsv: 'CSV importieren',
          attachment: 'Anhang',
          description:
            'Bitte formatiere deine Datei mit nur einer Spalte, einem Code pro Zeile und ohne Header (Spaltenüberschrift).',
          import: 'Importieren',
        },
        update: {
          editVoucherCode: 'Gutscheincode bearbeiten',
          enterCode: 'Code eingeben',
        },
        delete: {
          single: {
            title: 'Gutscheincode löschen',
            description: 'Möchtest du diesen Gutscheincode wirklich löschen?',
          },
          multiple: {
            title: 'Gutscheincodes löschen',
            description: 'Möchtest du diese Gutscheincodes wirklich löschen?',
          },
          all: {
            title: 'Alle Gutscheincodes löschen',
            description: 'Möchtest du wirklich alle Gutscheincodes für diese Kampagne löschen?',
          },
          confirm: 'Löschen',
        },
      },
      discountedProductsAndEvents: {
        title: 'Rabattierte Produkte und Veranstaltungen',
        merchandise: 'Waren',
        events: 'Veranstaltungen',
        singleResultSelected: '1 Suchergebnis ausgewählt',
        manyResultsSelected: (count: number) => `${count} Suchergebnisse ausgewählt`,
        allSelected: 'Alle Suchergebnisse ausgewählt',
        selectAll: 'Alle Suchergebnisse auswählen',
        switcherType: 'Typ',
        create: {
          productsAndEventsAssociation: 'Produkte- und Veranstaltungszuordnung',
          merchandise: 'Waren',
          events: 'Veranstaltungen',
          selectType: 'Typ auswählen',
          keepModalOpen: 'Modal geöffnet lassen',
          cancel: 'Abbrechen',
          add: 'Hinzufügen',
        },
        delete: {
          single: 'Du bist dabei, ein Produkt zu entfernen. Möchtest du fortfahren?',
          multiple: (items: number) => `Du bist dabei, ${items} Produkte zu entfernen. Möchtest du fortfahren?`,
          all: 'Du bist dabei, alle Produkte zu entfernen. Möchtest du fortfahren?',
          confirm: 'Löschen',
        },
        table: {
          emptyProducts: (shop: string) =>
            `Alle Produkte aus dem ${shop}-Shop werden rabattiert. Um diese Auswahl zu ändern, füge bitte spezifische Produkte hinzu.`,
          sku: 'SKU',
          artist: 'Künstler/Marke',
          categories: 'Kategorien',
          productType: 'Typ',
          motive: 'Motiv',
          color: 'Farbe',
          price: 'Preis',
          availability: 'Verfügbarkeit',
          noProductVariants: 'Keine Produktvarianten verfügbar.',
          description: 'Beschreibung',
          emptyEvents: 'Bitte füge Veranstaltungen hinzu, um zu beginnen.',
          event: 'Veranstaltung',
          venue: 'Veranstaltungsort',
          date: 'Datum',
          noEventsAvailable: 'Keine Veranstaltungen verfügbar.',
          pricingCategory: 'Preiskategorie',
          variant: 'Variante',
          priceWithDesc: 'Preis (€ netto/brutto)',
          noSearchResultsFound: 'Keine Suchergebnisse gefunden.',
        },
      },
    },
  },
  success: {
    addedItems: (item: string) => `${item} erfolgreich hinzugefügt`,
    deletedItems: (item: string) => `${item} erfolgreich gelöscht`,
    refundedItems: (item: string) => `${item} erfolgreich erstattet`,
  },
};

export default de;
